import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import Shell from '../dashboard/Shell';
import RecalculatePricesModal from './RecalculatePricesModal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { fetchMyStore, updateStore } from '../store/storeSlice';
import { getStoreUrl } from '../../config/platformConfig';
import PasswordConfirmModal from '../../components/PasswordConfirmModal';
import ChangeBankAccountModal from '../../components/ChangeBankAccountModal';
import CalendarIntegration from '../calendar/CalendarIntegration';
import GA4Settings from './GA4Settings';
import { useToast } from '../../components/ui/Toast';
import {
  Copy, Sliders, Bell, CreditCard, Truck, Plug, Lock, Wallet,
  Bike, BarChart3, Code2, Key, ShieldCheck, MonitorSmartphone,
  Landmark, Percent, AlertTriangle, Plus, ArrowRight, Check, AlertCircle,
  Eye, EyeOff, Clock, Coins, RefreshCw, Download, MapPin, Package,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FeatureGate from '../../components/FeatureGate';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { navigateToUpgrade } from '../subscription/upgradeNav';
import { useCurrency } from '../../contexts/CurrencyContext';
import { downloadVirtualTerminalPoster } from '../../utils/downloadVirtualTerminalPoster';
import './Settings.css';
import WooCommerceIntegration from './WooCommerceIntegration';
import OrderExpirySection from '../sales-tools/OrderExpirySection';

// Brand logos as inline SVG paths. Render with fill="currentColor" so the
// st-int-icon-* class's color rule controls the stroke color. Sized via the
// .st-int-icon svg rule (18×18).
const BrandIcon = {
  Gmail: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
    </svg>
  ),
  Sheets: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
    </svg>
  ),
  WhatsApp: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  ),
  WooCommerce: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M2.16 3h19.69c1.19 0 2.16.97 2.16 2.16v8.65c0 1.19-.97 2.16-2.16 2.16h-7.05L17.69 21l-7-4.04H2.16C.97 16.96 0 16 0 14.81V5.16C0 3.97.97 3 2.16 3zm.62 1.97c-.51.04-.89.48-.85.99.85 5.45 1.65 9.13 2.4 11.04.31.71.66 1.05 1.05 1.02.61-.05.96-.92 1.05-2.61.4-2.13.62-3.31.62-3.55 0 0 .8 1.55 2.4 4.4.27.49.59.74.95.66.31-.03.55-.34.7-.93.4-1.43.71-2.86.91-4.32.16-.7.31-1.5.45-2.39.04-.43.05-.81.04-1.13 0-.43-.18-.67-.51-.71-.55-.04-.93.32-1.13 1.08-.39 2.13-.65 4.05-.78 5.76l-1.97-3.76c-.18-.34-.39-.5-.65-.5-.38 0-.69.31-.87.92l-.92 3.19c-.18-1.49-.39-3.32-.61-5.49-.04-.65-.27-.95-.69-.88l-1.58.45z" />
    </svg>
  ),
};

// Modern SVG Eye Icons for password visibility toggle
const EyeOpenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5C7.45455 5 3.57273 7.90909 2 12C3.57273 16.0909 7.45455 19 12 19C16.5455 19 20.4273 16.0909 22 12C20.4273 7.90909 16.5455 5 12 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4L20 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6.71 6.71C4.19 8.28 2.64 10.47 2 12C3.57 16.09 7.45 19 12 19C13.89 19 15.67 18.5 17.24 17.65" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 5C12.67 5 13.33 5.07 13.97 5.2M19.73 8.27C20.8 9.35 21.58 10.61 22 12C20.43 16.09 16.55 19 12 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.88 9.88C9.33 10.44 9 11.18 9 12C9 13.66 10.34 15 12 15C12.82 15 13.56 14.67 14.12 14.12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

/**
 * Small presentational row for the Nationwide Shipping precondition
 * checklist. Green tick when satisfied, muted warn when not.
 * @param {{ok: boolean, label: string, hint?: string}} props
 */
function PreconditionRow({ ok, label, hint }) {
  return (
    <div className={`ta-precondition ${ok ? 'ta-precondition-ok' : 'ta-precondition-warn'}`}>
      <span className="ta-precondition-icon" aria-hidden="true">
        {ok ? <Check size={14} /> : <AlertCircle size={14} />}
      </span>
      <div className="ta-precondition-body">
        <span className="ta-precondition-label">{label}</span>
        {hint && <span className="ta-precondition-hint">{hint}</span>}
      </div>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { confirm } = useConfirm();
  const { getAxios, user } = useSecureAuth();
  const toast = useToast();
  const store = useSelector(s => s.store.current);
  const { status, error } = useSelector(s => s.store);
  const [activeTab, setActiveTab] = useState('general');
  const [terminalPosterDownloading, setTerminalPosterDownloading] = useState(false);

  // Phase 3.4 — deep-link from /settings#order-rules: force the
  // General tab + scroll to the Order rules card after it renders.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#order-rules') return;
    setActiveTab('general');
    const t = setTimeout(() => {
      const el = document.getElementById('order-rules');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, []);

  // Currency context
  const {
    accountCurrency,
    displayCurrency,
    switchCurrency,
    getAvailableCurrencies,
    formatMoney: formatMoneyFromContext,
    loading: currencyLoading
  } = useCurrency();
  const [switchingCurrency, setSwitchingCurrency] = useState(false);

  // Password change states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Billing states
  const [billingData, setBillingData] = useState({
    subscription: null,
    paymentMethods: [],
    invoices: []
  });
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [showBillingHistory, setShowBillingHistory] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState(false);
  // Card management states
  const [addingCard, setAddingCard] = useState(false);
  const [removingCardId, setRemovingCardId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const [removedCardIds, setRemovedCardIds] = useState([]);

  // Payment gateway states
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [loadingGateways, setLoadingGateways] = useState(false);
  const [togglingGateway, setTogglingGateway] = useState({});
  const [showTerminalModal, setShowTerminalModal] = useState(false);

  // Manual Bank Transfer state — feature-gated on payments.manual_transfer
  // AND requires a provisioned Paystack Virtual Terminal. Backend enforces
  // both; UI reads `canToggle` + `lockedReason` to render the row.
  const [manualTransfer, setManualTransfer] = useState({
    enabled: false,
    canToggle: false,
    lockedReason: null,
    virtualTerminal: null,
  });
  const [loadingManualTransfer, setLoadingManualTransfer] = useState(false);
  const [togglingManualTransfer, setTogglingManualTransfer] = useState(false);

  // COD is visible to every merchant in Payments, but only an eligible
  // merchant can enable it. The API owns the entitlement decision.
  const [codSettings, setCodSettings] = useState(null);
  const [loadingCodSettings, setLoadingCodSettings] = useState(false);
  const [togglingCod, setTogglingCod] = useState(false);
  const [codAnalytics, setCodAnalytics] = useState(null);
  const [showCodEnableModal, setShowCodEnableModal] = useState(false);
  const [codTermsAccepted, setCodTermsAccepted] = useState(false);

  // Tax settings states
  const [taxSettings, setTaxSettings] = useState({
    enabled: false,
    rate: 0,
    name: 'VAT',
    inclusive: false,
    description: ''
  });
  const [savedTaxSettings, setSavedTaxSettings] = useState(null);
  const [loadingTaxSettings, setLoadingTaxSettings] = useState(false);
  const [savingTaxSettings, setSavingTaxSettings] = useState(false);

  // Tip settings states
  const [tipSettings, setTipSettings] = useState({
    enabled: true,
    suggestedAmounts: [100, 200, 500],
    allowCustom: true,
    trackByEmployee: true
  });
  const [loadingTipSettings, setLoadingTipSettings] = useState(false);
  const [savingTipSettings, setSavingTipSettings] = useState(false);

  // KYC states
  const [kycStatus, setKycStatus] = useState(null);
  const [loadingKYC, setLoadingKYC] = useState(false);

  // Bank account states
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(false);
  const [passwordConfirmResolve, setPasswordConfirmResolve] = useState(null);
  const [revealedBankNumber, setRevealedBankNumber] = useState(false);
  const [bankRevealTimer, setBankRevealTimer] = useState(null);
  const [showChangeBankModal, setShowChangeBankModal] = useState(false);

  // POS Fee states
  const [posFeeData, setPosFeeData] = useState({
    balance: null,
    invoices: [],
    status: null,
    settings: null,
    availableCycles: []
  });
  const [loadingPosFees, setLoadingPosFees] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [savingPosSettings, setSavingPosSettings] = useState(false);

  // Service charge "who pays" states
  const [serviceChargePayer, setServiceChargePayer] = useState(null);
  const [serviceChargeChannels, setServiceChargeChannels] = useState([]);
  const [serviceChargeChannelPreferences, setServiceChargeChannelPreferences] = useState({});
  const [serviceChargeSource, setServiceChargeSource] = useState(null);
  const [loadingServiceCharge, setLoadingServiceCharge] = useState(false);
  const [savingServiceCharge, setSavingServiceCharge] = useState(false);

  // WhatsApp feature access
  const { hasAccess: hasWhatsAppAccess, loading: whatsAppFeatureLoading } = useFeatureAccess('orders.whatsapp');
  const { hasAccess: hasSheetsAccess } = useFeatureAccess('integrations.sheets');
  const { hasAccess: hasWooCommerceAccess } = useFeatureAccess('integrations.woocommerce');
  const { hasAccess: hasGA4Access } = useFeatureAccess('analytics.ga4');
  // Auto-disable WhatsApp Orders if user loses feature access (e.g., downgrade)
  useEffect(() => {
    const autoDisableWhatsAppIfNoAccess = async () => {
      // Wait until feature access check is complete and store is loaded
      if (whatsAppFeatureLoading || !store) return;

      // If WhatsApp is enabled but no longer has access, auto-disable
      if (store.whatsappOrdersEnabled && !hasWhatsAppAccess) {
        try {
          const axios = getAxios();
          await axios.post('/stores', {
            whatsappOrdersEnabled: false
          });
          dispatch(fetchMyStore(axios));
          toast.info('WhatsApp Orders has been disabled as it is no longer available on your current plan.');
        } catch (err) {
          console.error('Failed to auto-disable WhatsApp Orders:', err);
        }
      }
    };

    autoDisableWhatsAppIfNoAccess();
  }, [whatsAppFeatureLoading, hasWhatsAppAccess, store?.whatsappOrdersEnabled, dispatch, getAxios, toast]);

  // Chowdeck delivery feature access
  const { hasAccess: hasChowdeckAccess, loading: chowdeckFeatureLoading } = useFeatureAccess('delivery.chowdeck');
  const { hasAccess: hasPickupAccess, loading: pickupFeatureLoading } = useFeatureAccess('fulfillment.pickup');
  const [chowdeckSettings, setChowdeckSettings] = useState({
    enabled: false,
    pickupAddress: { address: '', city: '', state: '', landmark: '' },
    pickupContact: { name: '', phone: '', email: '' },
    shippingOrigin: null,
  });
  const [chowdeckLoading, setChowdeckLoading] = useState(true);
  const [chowdeckSaving, setChowdeckSaving] = useState(false);
  const [showChowdeckForm, setShowChowdeckForm] = useState(false);
  const [chowdeckPlatformConfigured, setChowdeckPlatformConfigured] = useState(false);

  // Wallet balance — used by the Chowdeck card's minimum-balance gate (₦5,000).
  // Fetched lazily when the integrations tab opens so we don't burn the
  // /wallet/balance endpoint for users who never touch this tab.
  const CHOWDECK_MIN_BALANCE = 5000;
  const [walletBalance, setWalletBalance] = useState(null);
  useEffect(() => {
    if (activeTab !== 'integrations') return;
    let cancelled = false;
    (async () => {
      try {
        const axios = getAxios();
        const res = await axios.get('/wallet/balance');
        if (cancelled) return;
        const bal = Number(res?.data?.balance ?? res?.data?.availableBalance ?? 0);
        setWalletBalance(Number.isFinite(bal) ? bal : 0);
      } catch (e) {
        if (!cancelled) setWalletBalance(0);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, getAxios]);
  const chowdeckMeetsMinimum = walletBalance == null ? true : walletBalance >= CHOWDECK_MIN_BALANCE;

  // Check if Chowdeck is configured at platform level (public endpoint)
  useEffect(() => {
    const checkPlatformStatus = async () => {
      try {
        const response = await fetch('/api/chowdeck/status');
        if (response.ok) {
          const data = await response.json();
          setChowdeckPlatformConfigured(data.configured || false);
        }
      } catch (error) {
        console.error('Error checking Chowdeck platform status:', error);
      }
    };
    checkPlatformStatus();
  }, []);

  // Fetch Chowdeck merchant settings (requires auth + feature access)
  useEffect(() => {
    const fetchChowdeckSettings = async () => {
      try {
        const response = await fetch('/api/chowdeck/settings', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const shippingOrigin = data.shippingOrigin || null;
            setChowdeckSettings({
              enabled: data.settings?.enabled || false,
              pickupAddress: data.settings?.pickupAddress || (shippingOrigin ? {
                address: shippingOrigin.address || '',
                city: shippingOrigin.city || '',
                state: shippingOrigin.state || '',
              } : { address: '', city: '', state: '', landmark: '' }),
              pickupContact: data.settings?.pickupContact || { name: '', phone: '', email: '' },
              shippingOrigin,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching Chowdeck settings:', error);
      } finally {
        setChowdeckLoading(false);
      }
    };

    if (!chowdeckFeatureLoading && hasChowdeckAccess) {
      fetchChowdeckSettings();
    } else {
      setChowdeckLoading(false);
    }
  }, [chowdeckFeatureLoading, hasChowdeckAccess]);

  // ── Terminal Africa nationwide shipping (Phase 4 — merchant onboarding) ──
  // Multi-carrier parcel delivery via GIG/DHL/Fez/Kwik/etc. Toggle lives
  // in Integrations tab; enable requires KYC APPROVED + defaultShippingLocation
  // set + wallet ≥ ₦5k + TOS acceptance.
  const { hasAccess: hasTerminalAccess, loading: terminalFeatureLoading } = useFeatureAccess('delivery.terminal_africa');
  const [terminalData, setTerminalData] = useState(null); // full /settings response
  const [terminalLoading, setTerminalLoading] = useState(true);
  const [terminalSaving, setTerminalSaving] = useState(false);
  const [terminalTosChecked, setTerminalTosChecked] = useState(false);
  const TERMINAL_MIN_BALANCE = 5000;

  useEffect(() => {
    if (activeTab !== 'integrations') return;
    let cancelled = false;
    (async () => {
      try {
        setTerminalLoading(true);
        const axios = getAxios();
        const r = await axios.get('/terminal-africa/settings');
        if (!cancelled && r?.data?.success) setTerminalData(r.data);
      } catch (e) {
        // 403 = no feature access — leave data null, card shows locked badge
        if (!cancelled) setTerminalData(null);
      } finally {
        if (!cancelled) setTerminalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, getAxios]);

  const terminalEnabled = Boolean(terminalData?.settings?.enabled);
  const terminalPreconditions = terminalData?.preconditions || {};
  const terminalCanEnable =
    terminalPreconditions.featureAccess &&
    terminalPreconditions.platformConfigured &&
    terminalPreconditions.kycApproved &&
    terminalPreconditions.hasDefaultShippingLocation &&
    terminalPreconditions.walletSufficient;

  const saveTerminalSettings = async ({ enabled, acceptTos = false }) => {
    try {
      setTerminalSaving(true);
      const axios = getAxios();
      const payload = { enabled };
      if (acceptTos) payload.tosAccepted = true;
      const r = await axios.put('/terminal-africa/settings', payload);
      if (r?.data?.success) {
        toast.success(r.data.message || 'Nationwide shipping updated.');
        // Reload full settings so preconditions/warnings reflect the new state
        const s = await axios.get('/terminal-africa/settings');
        if (s?.data?.success) setTerminalData(s.data);
        setTerminalTosChecked(false);
      } else {
        toast.error(r?.data?.error || 'Could not update settings.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update Terminal Africa settings.');
    } finally {
      setTerminalSaving(false);
    }
  };

  // ── Default shipping location (Phase 1 — canonical shipping origin) ─────
  // The Location this store ships online orders from. Consumed by Terminal
  // Africa + any future ShippingProvider integrations. Independent of
  // Location.isPrimary (which is the POS/inventory default).
  const [shipLocList, setShipLocList] = useState([]);
  const [shipLocSelectedId, setShipLocSelectedId] = useState(store?.defaultShippingLocationId || '');
  const [shipLocLoading, setShipLocLoading] = useState(false);
  const [shipLocSaving, setShipLocSaving] = useState(false);
  // Per-location stock coverage — {locationId, productsInStock, coveragePct}
  // Loaded parallel to the location list; may be null if the coverage
  // endpoint fails (we degrade gracefully — dropdown still works, no chips).
  const [shipLocCoverage, setShipLocCoverage] = useState(null);

  // Keep the local selection in sync with the store record whenever it
  // refreshes from Redux (e.g. after a fetchMyStore() elsewhere on the page).
  useEffect(() => {
    setShipLocSelectedId(store?.defaultShippingLocationId || '');
  }, [store?.defaultShippingLocationId]);

  // Lazy-load locations + coverage only when the shipping tab is opened.
  // Two calls run in parallel so the picker isn't blocked on the aggregate
  // query — if coverage is slow or fails, the picker still renders.
  useEffect(() => {
    if (activeTab !== 'shipping') return;
    let cancelled = false;
    (async () => {
      try {
        setShipLocLoading(true);
        const axios = getAxios();
        const [locRes, covRes] = await Promise.allSettled([
          axios.get('/locations'),
          axios.get('/stores/shipping-location-coverage'),
        ]);
        if (cancelled) return;

        if (locRes.status === 'fulfilled') {
          const list = Array.isArray(locRes.value?.data?.locations)
            ? locRes.value.data.locations
            : [];
          setShipLocList(list.filter(l => l.isActive && (l.adminStatus || 'ACTIVE') === 'ACTIVE'));
        } else {
          console.error('Locations fetch failed:', locRes.reason);
          toast.error('Could not load your locations.');
        }

        if (covRes.status === 'fulfilled' && covRes.value?.data?.success) {
          setShipLocCoverage(covRes.value.data);
        } else {
          // Non-fatal — picker still works without coverage chips.
          setShipLocCoverage(null);
        }
      } finally {
        if (!cancelled) setShipLocLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, getAxios, toast]);

  const shipLocDirty = (shipLocSelectedId || '') !== (store?.defaultShippingLocationId || '');
  const shipLocSelected = shipLocList.find(l => l.id === shipLocSelectedId) || null;

  // Coverage lookup helpers. Returns null if we don't have coverage data
  // yet or if the location isn't in the response (fresh Location added
  // after the coverage snapshot).
  const shipLocCoverageMap = useMemo(() => {
    if (!shipLocCoverage?.locations) return new Map();
    return new Map(shipLocCoverage.locations.map(c => [c.locationId, c]));
  }, [shipLocCoverage]);
  const shipLocSelectedCoverage = shipLocSelected
    ? shipLocCoverageMap.get(shipLocSelected.id) || null
    : null;
  const shipLocTotalTracked = shipLocCoverage?.totalTrackedProducts ?? null;
  const [pickupDrafts, setPickupDrafts] = useState({});
  const [pickupSavingId, setPickupSavingId] = useState(null);

  useEffect(() => {
    if (activeTab !== 'shipping') return;
    setPickupDrafts(Object.fromEntries(shipLocList.map((location) => [location.id, {
      offersPickup: Boolean(location.offersPickup),
      pickupInstructions: location.pickupInstructions || '',
      pickupPrepMinutes: location.pickupPrepMinutes ?? 30,
    }])));
  }, [activeTab, shipLocList]);

  const updatePickupDraft = (locationId, field, value) => {
    setPickupDrafts((current) => ({
      ...current,
      [locationId]: { ...current[locationId], [field]: value },
    }));
  };

  const isPickupDraftDirty = (locationId) => {
    const loc = shipLocList.find((l) => l.id === locationId);
    const draft = pickupDrafts[locationId];
    if (!loc || !draft) return false;
    return Boolean(loc.offersPickup) !== Boolean(draft.offersPickup)
      || (loc.pickupInstructions || '') !== (draft.pickupInstructions || '')
      || (loc.pickupPrepMinutes ?? 30) !== (draft.pickupPrepMinutes ?? 30);
  };

  const resetPickupDraft = (locationId) => {
    const loc = shipLocList.find((l) => l.id === locationId);
    if (!loc) return;
    setPickupDrafts((current) => ({
      ...current,
      [locationId]: {
        offersPickup: Boolean(loc.offersPickup),
        pickupInstructions: loc.pickupInstructions || '',
        pickupPrepMinutes: loc.pickupPrepMinutes ?? 30,
      },
    }));
  };

  const savePickupSettings = async (locationId) => {
    const draft = pickupDrafts[locationId];
    if (!draft) return;
    try {
      setPickupSavingId(locationId);
      const axios = getAxios();
      const response = await axios.patch(`/locations/${locationId}/pickup-settings`, draft);
      if (!response?.data?.success) throw new Error(response?.data?.error || 'Could not save pickup settings');
      setShipLocList((locations) => locations.map((location) => location.id === locationId
        ? { ...location, ...response.data.location }
        : location));
      toast.success('Pickup settings saved.');
    } catch (error) {
      toast.error(error?.response?.data?.error || error.message || 'Could not save pickup settings.');
    } finally {
      setPickupSavingId(null);
    }
  };

  // Coverage tone (green/amber/red) — used for the small chip next to
  // each dropdown option. Thresholds match the industry-standard traffic
  // light for stock availability (Shopify/Lightspeed use ~90/30 too).
  const coverageTone = (pct) => {
    if (pct == null) return 'neutral';
    if (pct >= 90) return 'good';
    if (pct >= 30) return 'warn';
    return 'bad';
  };
  const coverageLabel = (cov) => {
    if (!cov) return '';
    if (shipLocTotalTracked === 0) return 'No physical products';
    return `${cov.productsInStock}/${shipLocTotalTracked} in stock`;
  };

  const saveDefaultShippingLocation = async () => {
    try {
      setShipLocSaving(true);
      const axios = getAxios();
      const res = await axios.put('/stores/default-shipping-location', {
        locationId: shipLocSelectedId || null,
      });
      if (res?.data?.success) {
        toast.success(res.data.message || 'Default shipping location updated.');
        // Refresh the store slice so future page loads see the new value.
        dispatch(fetchMyStore(axios));
      } else {
        toast.error(res?.data?.error || 'Could not save.');
      }
    } catch (e) {
      const msg = e?.response?.data?.error || 'Could not save default shipping location.';
      toast.error(msg);
    } finally {
      setShipLocSaving(false);
    }
  };

  // Gmail integration states
  const [gmailStatus, setGmailStatus] = useState({
    connected: false,
    email: null,
    loading: true
  });
  const [gmailConnecting, setGmailConnecting] = useState(false);
  const [gmailTestingEmail, setGmailTestingEmail] = useState(false);

  // Integration modal state
  const [activeIntegrationModal, setActiveIntegrationModal] = useState(null);

  // Google Calendar integration states
  const [calendarStatus, setCalendarStatus] = useState({
    connected: false,
    email: null,
    calendarId: null,
    loading: true
  });
  const [calendarConnecting, setCalendarConnecting] = useState(false);

  // Google Sheets integration states
  const [sheetsStatus, setSheetsStatus] = useState({
    connected: false,
    email: null,
    loading: true
  });
  const [sheetsConnecting, setSheetsConnecting] = useState(false);
  const [sheetsExporting, setSheetsExporting] = useState(null);

  // WooCommerce integration states
  const [wooStatus, setWooStatus] = useState({
    connected: false,
    storeName: null,
    storeUrl: null,
    loading: true
  });

  // GA4 integration states
  const [ga4Status, setGa4Status] = useState({
    enabled: false,
    measurementId: null,
    loading: true
  });

  // Fetch Gmail integration status
  useEffect(() => {
    const fetchGmailStatus = async () => {
      try {
        const response = await fetch('/api/merchant/gmail/oauth/status', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setGmailStatus({
            connected: data.connected || false,
            email: data.email || null,
            loading: false
          });
        } else {
          setGmailStatus(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error fetching Gmail status:', error);
        setGmailStatus(prev => ({ ...prev, loading: false }));
      }
    };
    fetchGmailStatus();
  }, []);

  // Handle Gmail connect
  const connectGmail = async () => {
    setGmailConnecting(true);
    try {
      const response = await fetch('/api/merchant/gmail/oauth/authorize', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
        } else {
          toast.error('Failed to get authorization URL');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to connect Gmail');
      }
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      toast.error('Failed to connect Gmail');
    } finally {
      setGmailConnecting(false);
    }
  };

  // Handle Gmail disconnect
  const disconnectGmail = async () => {
    const confirmed = await confirm({
      title: 'Disconnect Gmail',
      message: 'Are you sure you want to disconnect your Gmail account? Customer emails will be sent from the platform email instead.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await fetch('/api/merchant/gmail/oauth/disconnect', {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setGmailStatus({
          connected: false,
          email: null,
          loading: false
        });
        toast.success('Gmail disconnected successfully');
      } else {
        toast.error('Failed to disconnect Gmail');
      }
    } catch (error) {
      console.error('Error disconnecting Gmail:', error);
      toast.error('Failed to disconnect Gmail');
    }
  };

  // Send test email via Gmail
  const sendTestEmail = async () => {
    setGmailTestingEmail(true);
    try {
      const response = await fetch('/api/merchant/gmail/oauth/test', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message || 'Test email sent successfully!');
      } else {
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setGmailTestingEmail(false);
    }
  };

  // Handle URL parameters from mobile app redirect (iOS)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const promo = params.get('promo');
    const tier = params.get('tier');

    // Set active tab from URL. Allow any known tab id so deep links from
    // Overview (e.g. /settings?tab=payments) land on the right tab.
    const KNOWN_TABS = new Set(['general', 'payments', 'billing', 'notifications', 'security', 'shipping', 'tax', 'team', 'integrations']);
    if (tab && KNOWN_TABS.has(tab)) {
      setActiveTab(tab);
    }

    // Deep-link an integration modal open from another surface (e.g. the
    // post-login modal's WhatsApp slide → ?tab=integrations&open=whatsapp).
    const openIntegration = params.get('open');
    const KNOWN_INTEGRATIONS = new Set(['gmail', 'calendar', 'sheets', 'whatsapp', 'chowdeck', 'woocommerce', 'ga4']);
    if (tab === 'integrations' && openIntegration && KNOWN_INTEGRATIONS.has(openIntegration)) {
      setActiveIntegrationModal(openIntegration);
    }

    // Strip promo from URL after read — promo carries into /upgrade/checkout
    // via the redirect below if a tier was also specified. Standalone
    // promo (no tier) is dropped since the merchant would need to pick
    // a plan first anyway; they can re-enter on the summary page.
    if (promo) {
      const newUrl = window.location.pathname + (tab ? `?tab=${tab}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    // Legacy deep-links (?tier=PRO&tab=billing) from trial banners /
    // upgrade emails / iOS redirect flow now bypass the old tier modal
    // entirely and land on the route-based /upgrade/checkout summary
    // for the requested tier. Discount code, if present, is carried
    // through so promo emails apply their offer automatically.
    if (tier && tab === 'billing') {
      const upperTier = tier.toUpperCase();
      const search = new URLSearchParams({
        tier: upperTier,
        action: 'upgrade',
        name: upperTier,
      });
      if (promo) search.set('discountCode', promo);
      navigate(`/upgrade/checkout?${search.toString()}`, { replace: true });
      return;
    }
  }, []);

  // Check for Gmail OAuth callback results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_connected') === 'true') {
      const email = params.get('email');
      setGmailStatus({
        connected: true,
        email: email || null,
        loading: false
      });
      toast.success(`Gmail connected successfully${email ? ': ' + email : ''}`);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('gmail_error')) {
      const error = params.get('gmail_error');
      const errorMessages = {
        'access_denied': 'Gmail access was denied',
        'missing_parameters': 'Missing required parameters',
        'connection_failed': 'Failed to connect Gmail',
        'server_error': 'Server error occurred'
      };
      toast.error(errorMessages[error] || 'Failed to connect Gmail');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  // Fetch Calendar integration status
  useEffect(() => {
    const fetchCalendarStatus = async () => {
      try {
        const response = await fetch('/api/merchant/calendar/oauth/status', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setCalendarStatus({
            connected: data.connected || false,
            email: data.email || null,
            calendarId: data.calendarId || null,
            loading: false
          });
        } else {
          setCalendarStatus(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error fetching Calendar status:', error);
        setCalendarStatus(prev => ({ ...prev, loading: false }));
      }
    };
    fetchCalendarStatus();
  }, []);

  // Handle Calendar connect
  const connectCalendar = async () => {
    setCalendarConnecting(true);
    try {
      // returnUrl=true asks the backend to send the Google auth URL as JSON
      // instead of issuing a 302 redirect. Without it, fetch() transparently
      // follows the redirect into accounts.google.com (no CORS headers) and
      // the request fails. Gmail/Sheets backends already default to JSON;
      // Calendar still defaults to redirect for the in-page browser flow,
      // hence this explicit flag.
      const response = await fetch('/api/merchant/calendar/oauth/authorize?returnUrl=true', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
        } else {
          toast.error('Failed to get authorization URL');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to connect Calendar');
      }
    } catch (error) {
      console.error('Error connecting Calendar:', error);
      toast.error('Failed to connect Calendar');
    } finally {
      setCalendarConnecting(false);
    }
  };

  // Handle Calendar disconnect
  const disconnectCalendar = async () => {
    const confirmed = await confirm({
      title: 'Disconnect Google Calendar',
      message: 'Are you sure you want to disconnect your Google Calendar?',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await fetch('/api/merchant/calendar/oauth/disconnect', {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setCalendarStatus({
          connected: false,
          email: null,
          calendarId: null,
          loading: false
        });
        toast.success('Calendar disconnected successfully');
      } else {
        toast.error('Failed to disconnect Calendar');
      }
    } catch (error) {
      console.error('Error disconnecting Calendar:', error);
      toast.error('Failed to disconnect Calendar');
    }
  };

  // Fetch Sheets integration status
  useEffect(() => {
    const fetchSheetsStatus = async () => {
      try {
        const response = await fetch('/api/merchant/sheets/oauth/status', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSheetsStatus({
            connected: data.connected || false,
            email: data.email || null,
            loading: false
          });
        } else {
          setSheetsStatus(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error fetching Sheets status:', error);
        setSheetsStatus(prev => ({ ...prev, loading: false }));
      }
    };
    fetchSheetsStatus();
  }, []);

  // Fetch WooCommerce integration status
  useEffect(() => {
    const fetchWooStatus = async () => {
      try {
        const response = await fetch('/api/merchant/woocommerce/status', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setWooStatus({
            connected: data.connected || false,
            storeName: data.integration?.storeName || null,
            storeUrl: data.integration?.storeUrl || null,
            loading: false
          });
        } else {
          setWooStatus(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error fetching WooCommerce status:', error);
        setWooStatus(prev => ({ ...prev, loading: false }));
      }
    };
    fetchWooStatus();
  }, []);

  // Fetch GA4 integration status
  useEffect(() => {
    const fetchGa4Status = async () => {
      try {
        const response = await fetch('/api/ga4/merchant/config', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setGa4Status({
            enabled: data.enabled || false,
            measurementId: data.measurementId || null,
            loading: false
          });
        } else {
          setGa4Status(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error fetching GA4 status:', error);
        setGa4Status(prev => ({ ...prev, loading: false }));
      }
    };
    fetchGa4Status();
  }, []);

  // Handle Sheets connect
  const connectSheets = async () => {
    setSheetsConnecting(true);
    try {
      const response = await fetch('/api/merchant/sheets/oauth/authorize', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
        } else {
          toast.error('Failed to get authorization URL');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to connect Google Sheets');
      }
    } catch (error) {
      console.error('Error connecting Sheets:', error);
      toast.error('Failed to connect Google Sheets');
    } finally {
      setSheetsConnecting(false);
    }
  };

  // Handle Sheets disconnect
  const disconnectSheets = async () => {
    const confirmed = await confirm({
      title: 'Disconnect Google Sheets',
      message: 'Are you sure you want to disconnect Google Sheets?',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await fetch('/api/merchant/sheets/oauth/disconnect', {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setSheetsStatus({
          connected: false,
          email: null,
          loading: false
        });
        toast.success('Google Sheets disconnected successfully');
      } else {
        toast.error('Failed to disconnect Google Sheets');
      }
    } catch (error) {
      console.error('Error disconnecting Sheets:', error);
      toast.error('Failed to disconnect Google Sheets');
    }
  };

  // Export customers to Google Sheets
  const exportCustomersToSheets = async () => {
    setSheetsExporting('customers');
    try {
      const response = await fetch('/api/merchant/sheets/export/customers', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message || 'Customers exported to Google Sheets!');
        if (data.spreadsheetUrl) {
          window.open(data.spreadsheetUrl, '_blank');
        }
      } else {
        toast.error(data.error || 'Failed to export customers');
      }
    } catch (error) {
      console.error('Error exporting customers:', error);
      toast.error('Failed to export customers');
    } finally {
      setSheetsExporting(null);
    }
  };

  // Export orders to Google Sheets
  const exportOrdersToSheets = async () => {
    setSheetsExporting('orders');
    try {
      const response = await fetch('/api/merchant/sheets/export/orders', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message || 'Orders exported to Google Sheets!');
        if (data.spreadsheetUrl) {
          window.open(data.spreadsheetUrl, '_blank');
        }
      } else {
        toast.error(data.error || 'Failed to export orders');
      }
    } catch (error) {
      console.error('Error exporting orders:', error);
      toast.error('Failed to export orders');
    } finally {
      setSheetsExporting(null);
    }
  };

  // Check for Calendar/Sheets OAuth callback results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Calendar callback
    if (params.get('calendar_connected') === 'true') {
      setCalendarStatus(prev => ({ ...prev, connected: true, loading: false }));
      toast.success('Google Calendar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('calendar_error')) {
      toast.error('Failed to connect Google Calendar');
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Sheets callback
    if (params.get('sheets_connected') === 'true') {
      const email = params.get('email');
      setSheetsStatus({
        connected: true,
        email: email || null,
        loading: false
      });
      toast.success('Google Sheets connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('sheets_error')) {
      toast.error('Failed to connect Google Sheets');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  // Save Chowdeck settings
  const saveChowdeckSettings = async (newSettings) => {
    setChowdeckSaving(true);
    try {
      const response = await fetch('/api/chowdeck/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enabled: Boolean(newSettings.enabled),
          pickupAddress: newSettings.pickupAddress,
          pickupContact: newSettings.pickupContact,
        })
      });
      const data = await response.json();
      if (data.success) {
        setChowdeckSettings({
          ...newSettings,
          ...(data.settings || {}),
          shippingOrigin: newSettings.shippingOrigin,
        });
        toast.success(newSettings.enabled ? 'Chowdeck delivery enabled' : 'Chowdeck delivery disabled');
        setShowChowdeckForm(false);
      } else {
        toast.error(data.error || 'Failed to save Chowdeck settings');
      }
    } catch (error) {
      console.error('Error saving Chowdeck settings:', error);
      toast.error('Failed to save Chowdeck settings');
    } finally {
      setChowdeckSaving(false);
    }
  };

  const hasChowdeckOrigin = Boolean(
    chowdeckSettings.pickupAddress?.address && chowdeckSettings.pickupAddress?.state
  );
  const chowdeckOriginLabel = [
    chowdeckSettings.pickupAddress?.address,
    chowdeckSettings.pickupAddress?.city,
    chowdeckSettings.pickupAddress?.state,
  ].filter(Boolean).join(', ');

  // Helper function to get full image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path; // Already a full URL
    // Remove /api suffix from backend URL and append the path
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
    const baseUrl = backendUrl.replace(/\/api$/, '');
    return `${baseUrl}${path}`;
  };
  const [formData, setFormData] = useState({
    storeName: '',
    storeDescription: '',
    storeUrl: '',
    notificationEmail: '',
    supportEmail: '',
    timezone: 'UTC',
    currency: 'NGN',
    language: 'en',
    requireAccountRegistration: true,
    // Pricing defaults — Phase 4.A.6 follow-on
    defaultMarkupPercent: '',
    markupAutoApply: true,
    emailNotifications: {
      orderConfirmation: true,
      orderUpdates: true,
      newCustomerRegistration: true
    },
    notifications: {
      email: true,
      push: true,
      sms: false
    }
  });

  // Load store data on component mount
  useEffect(() => {
    const axios = getAxios();
    dispatch(fetchMyStore(axios));
  }, [dispatch, getAxios]);

  // Update form data when store loads
  useEffect(() => {
    if (store) {
      setFormData(prev => ({
        ...prev,
        storeName: store.name || '',
        storeDescription: store.description || '',
        storeUrl: store.slug ? getStoreUrl(store.slug) : '',
        notificationEmail: store.settings?.notificationEmail || store.settings?.orderNotificationEmail || '',
        supportEmail: store.settings?.supportEmail || '',
        timezone: store.settings?.timezone || 'UTC',
        currency: store.settings?.currency || 'NGN',
        // requireAccountRegistration is a top-level field in the Store model, not in settings
        allowGuestCheckout: !(store.requireAccountRegistration !== false),
        requireAccountRegistration: store.requireAccountRegistration !== false,
        // Pricing defaults — Phase 4.A.6 follow-on
        defaultMarkupPercent: store.defaultMarkupPercent != null ? String(store.defaultMarkupPercent) : '',
        markupAutoApply: store.markupAutoApply !== false,
        emailNotifications: {
          orderConfirmation: store.settings?.emailNotifications?.orderConfirmation !== false,
          orderUpdates: store.settings?.emailNotifications?.orderUpdates !== false,
          newCustomerRegistration: store.settings?.emailNotifications?.newCustomerRegistration !== false
        },
        notifications: {
          email: store.settings?.notifications?.email !== false,
          push: store.settings?.notifications?.push !== false,
          sms: store.settings?.notifications?.sms || false
        }
      }));
    }
  }, [store]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name.startsWith('emailNotifications.')) {
        const field = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          emailNotifications: {
            ...prev.emailNotifications,
            [field]: checked
          }
        }));
      } else if (name.startsWith('notifications.')) {
        const field = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          notifications: {
            ...prev.notifications,
            [field]: checked
          }
        }));
      } else {
        // Make allowGuestCheckout and requireAccountRegistration mutually exclusive
        if (name === 'allowGuestCheckout' && checked) {
          setFormData(prev => ({
            ...prev,
            [name]: checked,
            requireAccountRegistration: false
          }));
        } else if (name === 'requireAccountRegistration' && checked) {
          setFormData(prev => ({
            ...prev,
            [name]: checked,
            allowGuestCheckout: false
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            [name]: checked
          }));
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      const saveData = {
        name: formData.storeName,
        description: formData.storeDescription,
        // Top-level Store columns — Phase 4.A.6 pricing defaults.
        // Empty string means "clear" (null in DB → product/category fall
        // through to their own override).
        defaultMarkupPercent: formData.defaultMarkupPercent === '' ? null : parseFloat(formData.defaultMarkupPercent),
        markupAutoApply: formData.markupAutoApply !== false,
        settings: {
          requireAccountRegistration: formData.requireAccountRegistration,
          notificationEmail: formData.notificationEmail || null,
          supportEmail: formData.supportEmail || null,
          emailNotifications: formData.emailNotifications,
          notifications: formData.notifications,
          timezone: formData.timezone,
          currency: formData.currency
        }
      };

      console.log('Saving store settings with data:', saveData);

      const axios = getAxios();
      const result = await dispatch(updateStore({
        payload: saveData,
        axiosInstance: axios
      })).unwrap();

      console.log('Store settings saved successfully:', result);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings: ' + (error.error || error.message || 'Unknown error'));
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (password.length < minLength) {
      return { valid: false, message: 'Password must be at least 8 characters long' };
    }
    if (!hasUpperCase) {
      return { valid: false, message: 'Password must contain at least one uppercase letter' };
    }
    if (!hasLowerCase) {
      return { valid: false, message: 'Password must contain at least one lowercase letter' };
    }
    if (!hasNumber) {
      return { valid: false, message: 'Password must contain at least one number' };
    }
    if (!hasSpecialChar) {
      return { valid: false, message: 'Password must contain at least one special character (!@#$%^&*...)' };
    }

    return { valid: true };
  };

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.warning('Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.warning('New passwords do not match');
      return;
    }

    const validation = validatePassword(passwordData.newPassword);
    if (!validation.valid) {
      toast.warning(validation.message);
      return;
    }

    try {
      setChangingPassword(true);
      const axios = getAxios();
      await axios.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      toast.success('Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password: ' + (error.response?.data?.error || error.message || 'Unknown error'));
    } finally {
      setChangingPassword(false);
    }
  };

  // Billing data fetcher
  const fetchBillingData = React.useCallback(async () => {
    try {
      setLoadingBilling(true);
      const axios = getAxios();

      // First, cleanup any duplicate payment methods silently
      try {
        await axios.post('/merchant/billing/payment-methods/cleanup-duplicates');
      } catch (cleanupError) {
        // Silently ignore cleanup errors - endpoint may not exist yet
        console.log('Duplicate cleanup skipped:', cleanupError.message);
      }

      // Fetch subscription, payment methods, and invoices in parallel
      const [subscriptionRes, paymentMethodsRes, invoicesRes] = await Promise.all([
        axios.get('/merchant/billing/subscription'),
        axios.get('/merchant/billing/payment-methods'),
        axios.get('/merchant/billing/invoices')
      ]);

      setBillingData({
        subscription: subscriptionRes.data.subscription,
        paymentMethods: paymentMethodsRes.data.paymentMethods || [],
        invoices: invoicesRes.data.invoices || []
      });
    } catch (error) {
      console.error('Error fetching billing data:', error);

      // Handle authentication errors
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }

      // Handle other errors
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      toast.error('Failed to load billing information: ' + errorMessage);
    } finally {
      setLoadingBilling(false);
    }
  }, [getAxios]);

  // Card management functions
  const handleAddCard = async () => {
    try {
      setAddingCard(true);
      const axios = getAxios();
      const response = await axios.post('/merchant/billing/payment-methods/add');

      if (response.data.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      }
    } catch (error) {
      console.error('Error adding card:', error);
      toast.error(error.response?.data?.error || 'Failed to add card');
    } finally {
      setAddingCard(false);
    }
  };

  const handleSetDefaultCard = async (cardId) => {
    try {
      setSettingDefaultId(cardId);
      const axios = getAxios();
      await axios.put(`/merchant/billing/payment-methods/${cardId}/default`);
      toast.success('Default payment method updated');
      fetchBillingData();
    } catch (error) {
      console.error('Error setting default card:', error);
      toast.error(error.response?.data?.error || 'Failed to set default card');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleRemoveCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      setRemovingCardId(cardId);
      const axios = getAxios();
      await axios.delete(`/merchant/billing/payment-methods/${cardId}`);
      toast.success('Payment method removed');
      // Track removed card to hide from UI immediately
      setRemovedCardIds(prev => [...prev, cardId]);
      fetchBillingData();
    } catch (error) {
      console.error('Error removing card:', error);
      toast.error(error.response?.data?.error || 'Failed to remove payment method');
    } finally {
      setRemovingCardId(null);
    }
  };

  // Fetch billing data when billing tab is active
  useEffect(() => {
    if (activeTab === 'billing' && !billingData.subscription) {
      fetchBillingData();
    }
  }, [activeTab, billingData.subscription, fetchBillingData]);

  // POS Fee data fetcher
  const fetchPosFeeData = React.useCallback(async () => {
    try {
      setLoadingPosFees(true);
      const axios = getAxios();

      const [balanceRes, invoicesRes, statusRes, settingsRes] = await Promise.all([
        axios.get('/merchant/pos-fees/balance'),
        axios.get('/merchant/pos-fees/invoices?limit=5'),
        axios.get('/merchant/pos-fees/status'),
        axios.get('/merchant/pos-fees/settings')
      ]);

      setPosFeeData({
        balance: balanceRes.data,
        invoices: invoicesRes.data.invoices || [],
        status: statusRes.data.posAccess || null,
        settings: settingsRes.data.settings || null,
        availableCycles: settingsRes.data.availableCycles || []
      });
    } catch (error) {
      console.error('Error fetching POS fee data:', error);
      // Silently fail - POS fees may not be applicable to all merchants
    } finally {
      setLoadingPosFees(false);
    }
  }, [getAxios]);

  // Pay POS invoice
  const handlePayPosInvoice = async (invoiceId) => {
    try {
      setPayingInvoice(invoiceId);
      const axios = getAxios();
      const response = await axios.post(`/merchant/pos-fees/invoices/${invoiceId}/pay`);

      if (response.data.paymentLink) {
        window.location.href = response.data.paymentLink;
      } else {
        toast.error('Failed to get payment link');
      }
    } catch (error) {
      console.error('Error paying invoice:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
    } finally {
      setPayingInvoice(null);
    }
  };

  // Update POS fee settings (billing cycle)
  const handleUpdatePosSettings = async (newCycle) => {
    try {
      setSavingPosSettings(true);
      const axios = getAxios();
      const response = await axios.put('/merchant/pos-fees/settings', {
        invoiceCycle: newCycle
      });

      if (response.data.success) {
        setPosFeeData(prev => ({
          ...prev,
          settings: response.data.settings
        }));
        toast.success('Billing cycle updated successfully');
      }
    } catch (error) {
      console.error('Error updating POS settings:', error);
      toast.error(error.response?.data?.error || 'Failed to update billing cycle');
    } finally {
      setSavingPosSettings(false);
    }
  };

  // Fetch POS fee data when billing tab is active
  useEffect(() => {
    if (activeTab === 'billing' && !posFeeData.balance) {
      fetchPosFeeData();
    }
  }, [activeTab, posFeeData.balance, fetchPosFeeData]);

  // Check for successful POS fee payment from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const posfee = urlParams.get('posfee');
    const invoiceId = urlParams.get('invoice');

    if (posfee === 'success' && invoiceId) {
      toast.success('Payment successful! Your invoice has been paid.');
      // Refresh the data
      fetchPosFeeData();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=billing');
    }
  }, [fetchPosFeeData]);

  // Handle payment callback from Paystack
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status');
    const action = urlParams.get('action');
    const tab = urlParams.get('tab');
    // Check both 'reference' (our param) and 'trxref' (Paystack's param)
    const reference = urlParams.get('reference') || urlParams.get('trxref');

    if (tab === 'billing' && action === 'card_added') {
      // Card was added successfully
      toast.success('Card added successfully!');
      fetchBillingData();

      // Remove query parameters from URL
      const newUrl = window.location.pathname + '?tab=billing';
      window.history.replaceState({}, '', newUrl);
      return;
    }

    // Handle tier change payment callback
    // Check for either payment_status=success OR a tier_change reference in URL
    const hasTierChangeRef = reference && reference.startsWith('tier_change_');
    const isPaymentSuccess = paymentStatus === 'success' && tab === 'billing';
    const isTierChangeCallback = tab === 'billing' && hasTierChangeRef;

    if (isPaymentSuccess || isTierChangeCallback) {
      // If we have a tier_change reference, verify with the backend
      if (hasTierChangeRef) {
        const verifyTierChange = async () => {
          try {
            const axios = getAxios();
            const response = await axios.get(`/merchant/billing/verify-tier-change/${reference}`);

            if (response.data.success) {
              if (response.data.alreadyProcessed) {
                toast.success('Your subscription has been updated successfully!');
              } else {
                toast.success(`Successfully upgraded to ${response.data.currentTier} plan!`);
              }
            } else {
              toast.error(response.data.error || 'Failed to verify tier change. Please contact support.');
            }
          } catch (error) {
            console.error('Error verifying tier change:', error);
            // Still show a success message since payment went through
            // The webhook should eventually process it
            toast.success('Payment successful! Your subscription is being updated...');
          }

          // Refresh billing data regardless
          fetchBillingData();
        };

        verifyTierChange();
      } else if (isPaymentSuccess) {
        // No reference but payment_status=success, just show success and refresh
        toast.success('Payment successful! Your subscription has been updated.');
        fetchBillingData();
      }

      // Remove query parameters from URL without reloading
      const newUrl = window.location.pathname + '?tab=billing';
      window.history.replaceState({}, '', newUrl);

      // Set active tab to billing
      setActiveTab('billing');
    }
  }, [fetchBillingData, getAxios]);

  // KYC functions
  const fetchKYCStatus = React.useCallback(async () => {
    try {
      setLoadingKYC(true);
      const axios = getAxios();
      const response = await axios.get('/kyc/status');

      if (response.data.success) {
        setKycStatus(response.data.kyc);
      }
    } catch (error) {
      console.error('Error fetching KYC status:', error);
      // Don't alert on KYC fetch error, just log it
    } finally {
      setLoadingKYC(false);
    }
  }, [getAxios]);

  // Payment gateway functions
  const fetchPaymentGateways = React.useCallback(async () => {
    try {
      setLoadingGateways(true);
      const axios = getAxios();
      const response = await axios.get('/merchant/payment-gateways/gateways');

      if (response.data.status === 'success') {
        setPaymentGateways(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
      toast.error('Failed to load payment gateways: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingGateways(false);
    }
  }, [getAxios]);

  // Manual Bank Transfer — fetch current toggle + gating info.
  const fetchManualTransfer = React.useCallback(async () => {
    try {
      setLoadingManualTransfer(true);
      const axios = getAxios();
      const response = await axios.get('/merchant/payment-gateways/manual-transfer');
      if (response.data.status === 'success') {
        const d = response.data.data || {};
        setManualTransfer({
          enabled: d.enabled === true,
          canToggle: d.canToggle === true,
          lockedReason: d.lockedReason || null,
          virtualTerminal: d.virtualTerminal || null,
        });
      }
    } catch (error) {
      // Silent — merchants on old backends (pre-Sprint 4) simply don't see
      // the row. Toast would be noise for a feature they don't yet have.
      console.warn('Manual transfer settings unavailable:', error?.response?.data?.error || error.message);
    } finally {
      setLoadingManualTransfer(false);
    }
  }, [getAxios]);

  // Manual Bank Transfer — flip the toggle. Backend rejects if the merchant
  // doesn't have the feature or VT isn't provisioned, so this is safe to
  // call optimistically only after checking canToggle in the UI.
  const toggleManualTransfer = async () => {
    if (!manualTransfer.canToggle) return;
    const next = !manualTransfer.enabled;
    try {
      setTogglingManualTransfer(true);
      const axios = getAxios();
      const response = await axios.put('/merchant/payment-gateways/manual-transfer', { enabled: next });
      if (response.data.status === 'success') {
        const d = response.data.data || {};
        setManualTransfer((prev) => ({
          ...prev,
          enabled: d.enabled === true,
        }));
        toast.success(next ? 'Bank transfer enabled' : 'Bank transfer disabled');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error('Failed to update bank transfer: ' + errorMessage);
    } finally {
      setTogglingManualTransfer(false);
    }
  };

  const fetchCodSettings = React.useCallback(async () => {
    try {
      setLoadingCodSettings(true);
      const axios = getAxios();
      const response = await axios.get('/merchant/cod/settings');
      setCodSettings(response.data?.cod || null);
    } catch (error) {
      console.warn('COD settings unavailable:', error?.response?.data?.error || error.message);
      setCodSettings(null);
    } finally {
      setLoadingCodSettings(false);
    }
  }, [getAxios]);

  const commitCodSetting = async (next) => {
    try {
      setTogglingCod(true);
      const axios = getAxios();
      const response = await axios.put('/merchant/cod/settings', {
        enabled: next,
        ...(next ? { termsVersion: codSettings.termsVersion } : {}),
      });
      setCodSettings(response.data?.cod || null);
      toast.success(next ? 'Cash on delivery enabled' : 'Cash on delivery disabled');
      setShowCodEnableModal(false);
      setCodTermsAccepted(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to update cash on delivery.');
    } finally {
      setTogglingCod(false);
    }
  };

  const toggleCod = async () => {
    if (!codSettings || togglingCod) return;
    if (!codSettings.enabled) {
      setCodTermsAccepted(false);
      setShowCodEnableModal(true);
      return;
    }
    const confirmed = await confirm(
      'Customers won’t see cash on delivery at checkout anymore. Orders already in progress stay with you.',
      { title: 'Turn off cash on delivery?', confirmText: 'Turn off', cancelText: 'Keep enabled', type: 'warning' }
    );
    if (!confirmed) return;
    commitCodSetting(false);
  };

  const fetchCodAnalytics = React.useCallback(async () => {
    try {
      const response = await getAxios().get('/merchant/cod/analytics');
      setCodAnalytics(response.data?.metrics || null);
    } catch (error) {
      setCodAnalytics(null);
    }
  }, [getAxios]);

  // Fetch tax settings
  const fetchTaxSettings = React.useCallback(async () => {
    try {
      setLoadingTaxSettings(true);
      const axios = getAxios();
      const response = await axios.get('/merchant/payment-gateways/tax-settings');

      if (response.data.status === 'success') {
        setTaxSettings(response.data.data);
        setSavedTaxSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tax settings:', error);
    } finally {
      setLoadingTaxSettings(false);
    }
  }, [getAxios]);

  // Fetch tip settings
  const fetchTipSettings = React.useCallback(async () => {
    try {
      setLoadingTipSettings(true);
      const axios = getAxios();
      const response = await axios.get('/merchant/payment-gateways/tip-settings');

      if (response.data.status === 'success') {
        setTipSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tip settings:', error);
    } finally {
      setLoadingTipSettings(false);
    }
  }, [getAxios]);

  // Save tip settings
  const saveTipSettings = async () => {
    try {
      setSavingTipSettings(true);
      const axios = getAxios();
      const response = await axios.put('/merchant/payment-gateways/tip-settings', tipSettings);

      if (response.data.status === 'success') {
        setTipSettings(response.data.data);
        toast.success('Tip settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving tip settings:', error);
      const errorMessage = error.response?.data?.error || error.message;
      toast.error('Failed to save tip settings: ' + errorMessage);
    } finally {
      setSavingTipSettings(false);
    }
  };

  // --- Service Charge Functions ---
  const fetchServiceChargePayer = React.useCallback(async () => {
    setLoadingServiceCharge(true);
    try {
      const axios = getAxios();
      const res = await axios.get('/merchant/payment-gateways/service-charge-payer');
      const data = res.data.data || res.data;
      setServiceChargePayer(data.whoPays || 'customer');
      setServiceChargeChannelPreferences(data.channelPreferences || {});
      setServiceChargeChannels(Array.isArray(data.channels) ? data.channels : []);
      setServiceChargeSource(data.source || 'tier_default');
    } catch {
      console.error('Failed to load service charge preference');
    } finally {
      setLoadingServiceCharge(false);
    }
  }, [getAxios]);

  const handleServiceChargePayerChange = async (whoPays) => {
    setSavingServiceCharge(true);
    try {
      const axios = getAxios();
      await axios.put('/merchant/payment-gateways/service-charge-payer', { whoPays });
      setServiceChargePayer(whoPays);
      setServiceChargeSource('merchant_override');
      toast.success(`Service charge will now be paid by the ${whoPays}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update preference');
    } finally {
      setSavingServiceCharge(false);
    }
  };

  const handleServiceChargeChannelChange = async (channel, whoPays) => {
    const nextPreferences = {
      ...serviceChargeChannelPreferences,
      [channel]: whoPays
    };

    setSavingServiceCharge(true);
    try {
      const axios = getAxios();
      const response = await axios.put('/merchant/payment-gateways/service-charge-payer', {
        channelPreferences: nextPreferences
      });
      const data = response.data.data || response.data;
      setServiceChargePayer(data.whoPays || nextPreferences.STOREFRONT || whoPays);
      setServiceChargeChannelPreferences(data.channelPreferences || nextPreferences);
      setServiceChargeChannels(Array.isArray(data.channels) ? data.channels : serviceChargeChannels);
      setServiceChargeSource(data.source || 'merchant_override');
      toast.success('Transaction fee preference saved');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update preference');
    } finally {
      setSavingServiceCharge(false);
    }
  };

  const saveTaxSettings = async () => {
    setSavingTaxSettings(true);
    try {
      const axios = getAxios();
      const response = await axios.put('/merchant/payment-gateways/tax-settings', {
        enabled: !!taxSettings.enabled,
        rate: Number(taxSettings.rate || 0),
        name: taxSettings.name || 'Tax',
        inclusive: !!taxSettings.inclusive
      });

      if (response.data.status === 'success') {
        setTaxSettings(response.data.data);
        setSavedTaxSettings(response.data.data);
        toast.success('Tax settings saved');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save tax settings');
    } finally {
      setSavingTaxSettings(false);
    }
  };

  const normalizeTaxSettings = (settings = {}) => ({
    enabled: !!settings.enabled,
    rate: Number(settings.rate || 0),
    name: String(settings.name || 'Tax').trim() || 'Tax',
    inclusive: !!settings.inclusive
  });

  const taxSettingsDirty = savedTaxSettings
    ? JSON.stringify(normalizeTaxSettings(taxSettings)) !== JSON.stringify(normalizeTaxSettings(savedTaxSettings))
    : false;
  const taxSettingsStatusText = taxSettingsDirty
    ? 'Unsaved changes. Save tax settings before checkout uses this tax.'
    : taxSettings.enabled
      ? `${taxSettings.name || 'Tax'} is active at ${taxSettings.rate || 0}%.`
      : 'No merchant tax is added to customer orders.';

  // Fetch payment gateways, tax settings, tip settings, KYC status, and service charge when payments tab is active
  useEffect(() => {
    if (activeTab === 'payments') {
      if (paymentGateways.length === 0) {
        fetchPaymentGateways();
      }
      fetchTaxSettings();
      fetchTipSettings();
      fetchServiceChargePayer();
      fetchManualTransfer();
      fetchCodSettings();
      if (!kycStatus) {
        fetchKYCStatus();
      }
    }
  }, [activeTab, paymentGateways.length, kycStatus, fetchPaymentGateways, fetchTaxSettings, fetchTipSettings, fetchKYCStatus, fetchServiceChargePayer, fetchManualTransfer, fetchCodSettings]);

  useEffect(() => {
    if (activeTab === 'payments' && codSettings?.enabled) fetchCodAnalytics();
    if (!codSettings?.enabled) setCodAnalytics(null);
  }, [activeTab, codSettings?.enabled, fetchCodAnalytics]);


  const togglePaymentGateway = async (gatewayName, currentlyEnabled) => {
    try {
      setTogglingGateway(prev => ({ ...prev, [gatewayName]: true }));
      const axios = getAxios();

      const endpoint = currentlyEnabled ? 'disable' : 'enable';
      const response = await axios.post(`/merchant/payment-gateways/gateways/${gatewayName}/${endpoint}`);

      if (response.data.status === 'success') {
        // Refresh gateway list
        await fetchPaymentGateways();
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Error toggling payment gateway:', error);
      const errorMessage = error.response?.data?.error || error.message;
      const requiresKYC = error.response?.data?.data?.requiresKYC;

      if (requiresKYC) {
        // Show KYC-specific error with link to KYC page
        const confirmed = await confirm('Would you like to complete KYC verification now?', {
          title: 'KYC Verification Required',
          confirmText: 'Complete KYC',
          cancelText: 'Cancel',
          type: 'warning'
        });
        if (confirmed) {
          window.location.href = '/kyc';
        }
      } else {
        toast.error('Failed to update payment gateway: ' + errorMessage);
      }
    } finally {
      setTogglingGateway(prev => ({ ...prev, [gatewayName]: false }));
    }
  };

  // Bank account password verification handler
  const handlePasswordConfirmation = () => {
    return new Promise((resolve) => {
      setPasswordConfirmResolve(() => resolve);
      setShowPasswordConfirmModal(true);
    });
  };

  // Settlement bank reveal — asks for password, then shows the full
  // account number for 30s. Same pattern the legacy
  // SecureBankAccountDisplay used; auto-hide guards against
  // shoulder-surfing if the merchant walks away.
  const handleBankReveal = async () => {
    if (revealedBankNumber) {
      setRevealedBankNumber(false);
      if (bankRevealTimer) { clearTimeout(bankRevealTimer); setBankRevealTimer(null); }
      return;
    }
    const confirmed = await handlePasswordConfirmation();
    if (confirmed) {
      setRevealedBankNumber(true);
      const t = setTimeout(() => { setRevealedBankNumber(false); setBankRevealTimer(null); }, 30000);
      setBankRevealTimer(t);
    }
  };

  useEffect(() => () => { if (bankRevealTimer) clearTimeout(bankRevealTimer); }, [bankRevealTimer]);

  const handlePasswordConfirm = async (password) => {
    try {
      const axios = getAxios();
      const response = await axios.post('merchant/auth/verify-password', { password });

      if (response.data.success) {
        return { success: true };
      } else {
        return { success: false, error: response.data.error || 'Invalid password' };
      }
    } catch (error) {
      console.error('Password verification error:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to verify password' };
    }
  };

  const handlePasswordModalClose = (success) => {
    setShowPasswordConfirmModal(false);
    if (passwordConfirmResolve) {
      passwordConfirmResolve(success);
      setPasswordConfirmResolve(null);
    }
  };

  const handleChangeBankAccountSuccess = async () => {
    // Refresh KYC status to show the updated bank account
    await fetchKYCStatus();
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const axios = getAxios();
      const response = await axios.get(`/merchant/billing/invoices/${invoiceId}/download`, {
        responseType: 'blob' // Important: tell axios to expect binary data
      });

      // Create a blob URL from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDownloadTerminalPoster = async () => {
    try {
      setTerminalPosterDownloading(true);
      await downloadVirtualTerminalPoster();
      toast.success('Terminal banner downloaded');
    } catch (error) {
      console.error('Error downloading terminal banner:', error);
      toast.error(error.message || 'Failed to download terminal banner');
    } finally {
      setTerminalPosterDownloading(false);
    }
  };

  const copyTerminalValue = async (value, label = 'Detail') => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy — copy manually');
    }
  };

  const copyTerminalDetails = async () => {
    const details = [
      `Bank Name: ${store?.virtualTerminalBank || 'Paystack-Titan'}`,
      `Account Number: ${store?.virtualTerminalAccountNumber || ''}`,
      `Account Name: ${store?.virtualTerminalAccountName || store?.name || ''}`,
      `Terminal Code: ${store?.paystackVirtualTerminalCode || ''}`,
    ].filter(line => !line.endsWith(': ')).join('\n');

    await copyTerminalValue(details, 'Terminal details');
  };

  const viewTerminalTransactions = () => {
    window.location.href = '/wallet?source=VIRTUAL_TERMINAL';
  };

  const handleToggleAutoRenew = async () => {
    setTogglingAutoRenew(true);
    try {
      const axios = getAxios();
      const newAutoRenewValue = !billingData.subscription?.activeSubscription?.autoRenew;

      const response = await axios.put('/merchant/billing/auto-renew', {
        autoRenew: newAutoRenewValue
      });

      if (response.data.success) {
        // Update local state
        setBillingData(prev => ({
          ...prev,
          subscription: {
            ...prev.subscription,
            activeSubscription: {
              ...prev.subscription.activeSubscription,
              autoRenew: newAutoRenewValue
            }
          }
        }));
        toast.success(`Auto-renewal ${newAutoRenewValue ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error('Error toggling auto-renew:', error);
      toast.error('Failed to toggle auto-renewal: ' + (error.response?.data?.error || error.message));
    } finally {
      setTogglingAutoRenew(false);
    }
  };

  const formatCurrency = (amount, currency = null) => {
    // Use context's formatMoney if available, with fallback
    if (formatMoneyFromContext) {
      return formatMoneyFromContext(amount, currency);
    }
    // Fallback for when context isn't ready
    const symbols = {
      NGN: '₦',
      GHS: 'GH₵',
      KES: 'KSh',
      TZS: 'TSh',
      UGX: 'USh',
      USD: '$',
      EUR: '€',
      GBP: '£'
    };
    const curr = currency || 'NGN';
    return `${symbols[curr] || curr} ${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const tabs = [
    { id: 'general',       label: 'General',          Icon: Sliders },
    { id: 'notifications', label: 'Notifications',    Icon: Bell },
    { id: 'payments',      label: 'Payment gateways', Icon: CreditCard },
    { id: 'shipping',      label: 'Shipping & fulfillment', Icon: Truck },
    { id: 'integrations',  label: 'Integrations',     Icon: Plug },
    { id: 'security',      label: 'Security',         Icon: Lock },
    { id: 'billing',       label: 'Billing',          Icon: Wallet },
  ];

  return (
    <Shell>
      <div className="st-page">
        {/* Header */}
        <header className="st-header">
          <h1 className="st-title">Settings</h1>
          <p className="st-subtitle">Manage your account settings and preferences.</p>
          {user?.merchantCode && (
            <div className="st-meta">
              <span>Merchant ID:</span>
              <span className="st-meta-mono">{user.merchantCode}</span>
              <button
                type="button"
                className="st-meta-copy"
                onClick={() => {
                  navigator.clipboard?.writeText(user.merchantCode).then(
                    () => toast.success('Merchant ID copied'),
                    () => toast.error('Could not copy — copy manually'),
                  );
                }}
                title="Copy Merchant ID"
                aria-label="Copy Merchant ID"
              >
                <Copy size={12} aria-hidden="true" />
              </button>
            </div>
          )}
          {error && (
            <div className="st-banner st-banner-error" role="alert">
              <span>{error}</span>
            </div>
          )}
        </header>

        <div className="st-layout">
          {/* Sidebar nav — horizontal scroll on mobile, sticky vertical on desktop */}
          <nav className="st-nav" aria-label="Settings sections">
            {tabs.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`st-nav-btn ${activeTab === id ? 'is-active' : ''}`}
                aria-current={activeTab === id ? 'page' : undefined}
              >
                <Icon size={16} aria-hidden="true" />
                <span>{label}</span>
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="st-content">
            {activeTab === 'general' && (
              <>
                {/* Store profile */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Store profile</h2>
                      <p className="st-card-sub">The basics customers see on your storefront and receipts.</p>
                    </div>
                    <label className="st-field">
                      <span className="st-field-label">Store name</span>
                      <input
                        className="st-input"
                        name="storeName"
                        value={formData.storeName}
                        onChange={handleInputChange}
                        placeholder="e.g. Gbotemi Stores"
                      />
                    </label>
                    <label className="st-field">
                      <span className="st-field-label">Store description</span>
                      <textarea
                        className="st-textarea"
                        name="storeDescription"
                        value={formData.storeDescription}
                        onChange={handleInputChange}
                        placeholder="One or two lines about what you sell."
                      />
                    </label>
                  </div>
                </section>

                {/* Pricing defaults — Phase 4.A.6 follow-on. The anchor
                    lets product/category fields deep-link the merchant
                    straight to this card. */}
                <section className="st-card st-pricing-card" id="pricing-defaults">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Pricing defaults</h2>
                      <p className="st-card-sub">
                        Set once. Every new product inherits it. Categories and individual products can still override.
                      </p>
                    </div>

                    {/* Markup input — visually emphasised as the primary
                        knob, with a clear unit suffix and tight layout. */}
                    <div className="st-pricing-row">
                      <div className="st-pricing-row-copy">
                        <div className="st-pricing-row-label">Default markup</div>
                        <div className="st-pricing-row-sub">
                          Suggests a sell price for new stock: <strong>cost × (1 + markup%)</strong>. Blank means no automatic suggestion.
                        </div>
                      </div>
                      <div className="st-pricing-row-control">
                        <div className="st-pricing-input-wrap">
                          <input
                            type="number"
                            min="0"
                            max="999.99"
                            step="0.01"
                            name="defaultMarkupPercent"
                            className="st-pricing-input"
                            value={formData.defaultMarkupPercent}
                            onChange={handleInputChange}
                            placeholder="0"
                          />
                          <span className="st-pricing-input-suffix">%</span>
                        </div>
                      </div>
                    </div>

                    {/* Auto-apply toggle — same horizontal layout for
                        consistency, switch-style on the right. */}
                    <div className="st-pricing-row">
                      <div className="st-pricing-row-copy">
                        <div className="st-pricing-row-label">Auto-apply on receive</div>
                        <div className="st-pricing-row-sub">
                          When on, receiving new stock updates the sell price automatically unless the merchant unchecks. When off, the merchant has to opt-in each time.
                        </div>
                      </div>
                      <div className="st-pricing-row-control">
                        <label className="st-pricing-switch">
                          <input
                            type="checkbox"
                            checked={formData.markupAutoApply !== false}
                            onChange={(e) => setFormData((p) => ({ ...p, markupAutoApply: e.target.checked }))}
                          />
                          <span className="st-pricing-switch-track">
                            <span className="st-pricing-switch-thumb" />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Recalculate existing prices — opens the bulk
                        preview/apply modal. Sits at the bottom because
                        it's a destructive-ish operation that touches
                        many product prices at once. */}
                    <div className="st-pricing-row st-pricing-row--cta">
                      <div className="st-pricing-row-copy">
                        <div className="st-pricing-row-label">Recalculate existing product prices</div>
                        <div className="st-pricing-row-sub">
                          Tweaking the markup doesn't auto-bump products that already have a sell price. Open the recalculator to preview which products would change and apply selectively.
                        </div>
                      </div>
                      <div className="st-pricing-row-control">
                        <button
                          type="button"
                          className="st-pricing-cta"
                          onClick={() => setShowRecalculateModal(true)}
                        >
                          Open recalculator
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Email destinations */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Email destinations</h2>
                      <p className="st-card-sub">Optional — where to send order alerts and what customers see as your support address.</p>
                    </div>
                    <div className="st-grid st-grid-2col">
                      <label className="st-field">
                        <span className="st-field-label">Order notification email</span>
                        <input
                          className="st-input"
                          type="email"
                          name="notificationEmail"
                          value={formData.notificationEmail}
                          onChange={handleInputChange}
                          placeholder="orders@yourbusiness.com"
                        />
                        <p className="st-field-hint">Where new-order alerts land. Defaults to your account email.</p>
                      </label>
                      <label className="st-field">
                        <span className="st-field-label">Customer support email</span>
                        <input
                          className="st-input"
                          type="email"
                          name="supportEmail"
                          value={formData.supportEmail}
                          onChange={handleInputChange}
                          placeholder="support@yourbusiness.com"
                        />
                        <p className="st-field-hint">Shown in customer-facing emails as your reply-to.</p>
                      </label>
                    </div>
                  </div>
                </section>

                {/* Checkout */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Checkout</h2>
                      <p className="st-card-sub">Decide how strictly to gate account creation at checkout.</p>
                    </div>
                    <div className="st-check">
                      <label className="st-check-row">
                        <input
                          type="checkbox"
                          id="allowGuestCheckout"
                          name="allowGuestCheckout"
                          checked={formData.allowGuestCheckout}
                          onChange={handleInputChange}
                        />
                        <div className="st-check-row-text">
                          <span className="st-check-label">Allow guest checkout</span>
                          <span className="st-check-sub">Lets customers buy without creating a Revstar customer account.</span>
                        </div>
                      </label>
                      <label className="st-check-row">
                        <input
                          type="checkbox"
                          id="requireAccountRegistration"
                          name="requireAccountRegistration"
                          checked={formData.requireAccountRegistration}
                          onChange={handleInputChange}
                        />
                        <div className="st-check-row-text">
                          <span className="st-check-label">Require account registration for orders</span>
                          <span className="st-check-sub">Forces every customer to sign up before they can place an order.</span>
                        </div>
                      </label>
                    </div>
                    <p className="st-field-hint">
                      {formData.allowGuestCheckout
                        ? 'Currently: customers can check out without creating an account.'
                        : 'Currently: all customers must create an account before placing orders.'}
                    </p>
                  </div>
                </section>

                {/* Regional */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Regional</h2>
                      <p className="st-card-sub">Timezone for analytics + the currency customers pay in.</p>
                    </div>
                    <div className="st-grid st-grid-2col">
                      <label className="st-field">
                        <span className="st-field-label">Timezone</span>
                        <select
                          className="st-select"
                          name="timezone"
                          value={formData.timezone}
                          onChange={handleInputChange}
                        >
                          <option value="UTC">UTC</option>
                          <option value="Africa/Lagos">Africa/Lagos (GMT+1)</option>
                          <option value="America/New_York">America/New_York (GMT-5)</option>
                          <option value="Europe/London">Europe/London (GMT+0)</option>
                        </select>
                      </label>

                      <label className="st-field">
                        <span className="st-field-label">Store currency</span>
                        <div className="st-readonly">
                          <span className="st-readonly-value">
                            {formData.currency === 'NGN' && '₦ NGN — Nigerian Naira'}
                            {formData.currency === 'GHS' && 'GH₵ GHS — Ghanaian Cedi'}
                            {formData.currency === 'KES' && 'KSh KES — Kenyan Shilling'}
                            {formData.currency === 'TZS' && 'TSh TZS — Tanzanian Shilling'}
                            {formData.currency === 'UGX' && 'USh UGX — Ugandan Shilling'}
                            {!['NGN', 'GHS', 'KES', 'TZS', 'UGX'].includes(formData.currency) && formData.currency}
                          </span>
                          <span className="st-readonly-tag">Locked</span>
                        </div>
                        <p className="st-field-hint">
                          Your base currency for pricing. To change it, <a href="mailto:support@userevstar.com">contact support</a>.
                        </p>
                      </label>

                      <label className="st-field">
                        <span className="st-field-label">Display currency</span>
                        <select
                          className="st-select"
                          value={displayCurrency}
                          onChange={async (e) => {
                            const newCurrency = e.target.value;
                            setSwitchingCurrency(true);
                            const result = await switchCurrency(newCurrency);
                            setSwitchingCurrency(false);
                            if (result.success) {
                              toast.success(`Display currency switched to ${newCurrency}`);
                            } else {
                              toast.error(result.error || 'Failed to switch currency');
                            }
                          }}
                          disabled={switchingCurrency || currencyLoading}
                        >
                          {getAvailableCurrencies().map((currency) => (
                            <option key={currency.code} value={currency.code}>
                              {currency.symbol} {currency.code} — {currency.name}
                            </option>
                          ))}
                        </select>
                        <p className="st-field-hint">
                          How amounts are displayed in your dashboard. Switch between local currency and USD anytime.
                          {switchingCurrency && ' Switching…'}
                        </p>
                      </label>
                    </div>

                    <div className="st-card-foot">
                      <button
                        type="button"
                        className="st-btn st-btn-primary"
                        onClick={handleSave}
                        disabled={status === 'loading'}
                      >
                        {status === 'loading' ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </div>
                </section>

                {/* Phase 3.4 — Order rules (auto-cancel unpaid orders).
                    Inline section, no separate route. */}
                <OrderExpirySection />

                {/* Business preferences — surfaces UI-only opt-outs so
                    merchants can hide feature areas they don't use. This
                    is not access control (the tier/permission gates still
                    apply); it's cosmetic navigation cleanup. */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Business preferences</h2>
                      <p className="st-card-sub">Hide features you don't use so they don't clutter your navigation.</p>
                    </div>
                    <div className="st-pricing-row">
                      <div className="st-pricing-row-copy">
                        <div className="st-pricing-row-label">Show Point of Sale</div>
                        <div className="st-pricing-row-sub">
                          Turn off if your business is online-only. POS data (past orders, invoices) stays intact — this only affects the sidebar and navigation.
                        </div>
                      </div>
                      <div className="st-pricing-row-control">
                        <label className="st-pricing-switch">
                          <input
                            type="checkbox"
                            checked={store?.settings?.posOnboardingChoice !== 'dismissed'}
                            onChange={async (e) => {
                              const nextChoice = e.target.checked ? 'active' : 'dismissed';
                              try {
                                const nextSettings = { ...(store?.settings || {}), posOnboardingChoice: nextChoice };
                                await dispatch(updateStore({ payload: { settings: nextSettings }, axiosInstance: getAxios() })).unwrap();
                                toast.success(nextChoice === 'active' ? 'POS shown in navigation' : 'POS hidden from navigation');
                              } catch {
                                toast.error('Could not save preference');
                              }
                            }}
                          />
                          <span className="st-pricing-switch-track">
                            <span className="st-pricing-switch-thumb" />
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}

            {activeTab === 'notifications' && (
              <>
                {/* Customer order emails */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Customer order emails</h2>
                      <p className="st-card-sub">Which transactional emails your customers receive about their orders.</p>
                    </div>
                    <div className="st-check">
                      <label className="st-check-row">
                        <input
                          type="checkbox"
                          id="orderConfirmation"
                          name="emailNotifications.orderConfirmation"
                          checked={formData.emailNotifications.orderConfirmation}
                          onChange={handleInputChange}
                        />
                        <div className="st-check-row-text">
                          <span className="st-check-label">Order confirmation</span>
                          <span className="st-check-sub">Sent the moment a customer places an order.</span>
                        </div>
                      </label>
                      <label className="st-check-row">
                        <input
                          type="checkbox"
                          id="orderUpdates"
                          name="emailNotifications.orderUpdates"
                          checked={formData.emailNotifications.orderUpdates}
                          onChange={handleInputChange}
                        />
                        <div className="st-check-row-text">
                          <span className="st-check-label">Order status updates</span>
                          <span className="st-check-sub">Sent when an order moves to confirmed, shipped, delivered, etc.</span>
                        </div>
                      </label>
                      <label className="st-check-row">
                        <input
                          type="checkbox"
                          id="newCustomerRegistration"
                          name="emailNotifications.newCustomerRegistration"
                          checked={formData.emailNotifications.newCustomerRegistration}
                          onChange={handleInputChange}
                        />
                        <div className="st-check-row-text">
                          <span className="st-check-label">New-customer alerts</span>
                          <span className="st-check-sub">Notify you when someone creates a customer account on your store.</span>
                        </div>
                      </label>
                    </div>
                  </div>
                </section>

                {/* How we reach you */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">How we reach you</h2>
                      <p className="st-card-sub">Channels Revstar uses to send platform alerts to you (not your customers).</p>
                    </div>

                    <div>
                      <div className="st-toggle-row">
                        <div className="st-toggle-text">
                          <span className="st-toggle-label">Email</span>
                          <span className="st-toggle-sub">Platform alerts, weekly summaries, billing reminders.</span>
                        </div>
                        <label className="st-toggle">
                          <input
                            type="checkbox"
                            name="notifications.email"
                            checked={formData.notifications.email}
                            onChange={handleInputChange}
                          />
                          <span className="st-toggle-track"></span>
                        </label>
                      </div>

                      <div className="st-toggle-row">
                        <div className="st-toggle-text">
                          <span className="st-toggle-label">Push notifications</span>
                          <span className="st-toggle-sub">In-browser alerts for new orders and time-sensitive events.</span>
                        </div>
                        <label className="st-toggle">
                          <input
                            type="checkbox"
                            name="notifications.push"
                            checked={formData.notifications.push}
                            onChange={handleInputChange}
                          />
                          <span className="st-toggle-track"></span>
                        </label>
                      </div>

                      <div className="st-toggle-row">
                        <div className="st-toggle-text">
                          <span className="st-toggle-label">SMS</span>
                          <span className="st-toggle-sub">Sent to your phone for critical alerts only.</span>
                        </div>
                        <label className="st-toggle">
                          <input
                            type="checkbox"
                            name="notifications.sms"
                            checked={formData.notifications.sms}
                            onChange={handleInputChange}
                          />
                          <span className="st-toggle-track"></span>
                        </label>
                      </div>
                    </div>

                    <div className="st-card-foot">
                      <button
                        type="button"
                        className="st-btn st-btn-primary"
                        onClick={handleSave}
                        disabled={status === 'loading'}
                      >
                        {status === 'loading' ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </div>
                </section>
              </>
            )}

            {activeTab === 'shipping' && (
              <>
                {/* Default shipping location — canonical shipping-from address */}
                <section className="st-card ship-loc-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Shipping & fulfillment origin</h2>
                      <p className="st-card-sub">
                        Pick the location online orders ship from or customers collect from.
                        Delivery integrations use this address for quotes, waybills, and rider pickup.
                      </p>
                    </div>

                    {shipLocLoading ? (
                      <div className="ship-loc-skeleton" aria-hidden="true">
                        <div className="ship-loc-skeleton-row" />
                        <div className="ship-loc-skeleton-row" style={{ width: '70%' }} />
                      </div>
                    ) : shipLocList.length === 0 ? (
                      <div className="ship-loc-empty">
                        <div className="ship-loc-empty-icon" aria-hidden="true">
                          <MapPin size={20} />
                        </div>
                        <div className="ship-loc-empty-body">
                          <div className="ship-loc-empty-title">You haven't added any locations yet</div>
                          <p className="ship-loc-empty-sub">Add at least one warehouse or shop address so deliveries know where to pick up from.</p>
                          <button
                            type="button"
                            className="st-btn st-btn-primary"
                            onClick={() => navigate('/locations')}
                          >
                            <Plus size={14} aria-hidden="true" />
                            <span>Add a location</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="st-field">
                          <label className="st-field-label" htmlFor="ship-loc-select">
                            Ship or collect from
                          </label>
                          <select
                            id="ship-loc-select"
                            className="st-select"
                            value={shipLocSelectedId}
                            onChange={(e) => setShipLocSelectedId(e.target.value)}
                            disabled={shipLocSaving}
                          >
                            <option value="">— No default (customer sees carriers based on delivery zone) —</option>
                            {shipLocList.map((loc) => {
                              const label = [loc.name, loc.city, loc.state]
                                .filter(Boolean)
                                .join(' · ');
                              const cov = shipLocCoverageMap.get(loc.id);
                              // Native <select> options don't render styled
                              // pills, so the coverage number is appended
                              // in-line. Chip visualization sits below the
                              // select for the currently-highlighted item.
                              const covSuffix = cov && shipLocTotalTracked > 0
                                ? `  ·  ${cov.productsInStock}/${shipLocTotalTracked} in stock`
                                : '';
                              return (
                                <option key={loc.id} value={loc.id}>
                                  {label}
                                  {loc.isPrimary ? ' (Primary)' : ''}
                                  {covSuffix}
                                </option>
                              );
                            })}
                          </select>

                          {/* Coverage legend + overall footprint sits below
                              the picker. Non-native chips are impossible
                              inside <option>, so the tone is exposed here
                              via the preview card instead. */}
                          {shipLocCoverage && shipLocTotalTracked > 0 && (
                            <div className="ship-loc-legend" role="note">
                              <span className="ship-loc-legend-dot ship-loc-tone-good" aria-hidden="true" />
                              <span className="ship-loc-legend-txt">
                                {shipLocTotalTracked} physical product{shipLocTotalTracked === 1 ? '' : 's'} tracked
                              </span>
                            </div>
                          )}

                          <p className="st-field-hint">
                            Different from your Primary POS location — pick the warehouse or shop
                            you actually fulfill online orders from.
                          </p>
                        </div>

                        {shipLocSelected && (
                          <div className="ship-loc-preview" role="group" aria-label="Selected shipping origin address">
                            <div className="ship-loc-preview-head">
                              <span className="ship-loc-preview-icon" aria-hidden="true">
                                <Package size={16} />
                              </span>
                              <div className="ship-loc-preview-name">{shipLocSelected.name}</div>
                              {shipLocSelectedCoverage && shipLocTotalTracked > 0 && (
                                <span
                                  className={`ship-loc-coverage-chip ship-loc-tone-${coverageTone(shipLocSelectedCoverage.coveragePct)}`}
                                  title={`${shipLocSelectedCoverage.productsInStock} of ${shipLocTotalTracked} physical products in stock at this location`}
                                >
                                  {shipLocSelectedCoverage.coveragePct}% stocked
                                </span>
                              )}
                            </div>
                            <div className="ship-loc-preview-body">
                              {[
                                shipLocSelected.address,
                                shipLocSelected.city,
                                shipLocSelected.state,
                                shipLocSelected.country,
                                shipLocSelected.zipCode,
                              ]
                                .filter(Boolean)
                                .join(', ') || 'No address details on file. Update this location in Locations.'}
                            </div>
                            {shipLocSelected.phone && (
                              <div className="ship-loc-preview-meta">Contact · {shipLocSelected.phone}</div>
                            )}
                            {shipLocSelectedCoverage && shipLocTotalTracked > 0 && (
                              <div className={`ship-loc-preview-coverage ship-loc-tone-${coverageTone(shipLocSelectedCoverage.coveragePct)}`}>
                                <span className="ship-loc-coverage-num">
                                  {shipLocSelectedCoverage.productsInStock} of {shipLocTotalTracked}
                                </span>
                                <span className="ship-loc-coverage-txt">
                                  {' '}physical products are in stock at this location.
                                  {shipLocSelectedCoverage.coveragePct < 30 && (
                                    <>
                                      {' '}<a href="/locations" className="ship-loc-coverage-link">Transfer stock</a> so
                                      shipments actually leave from here.
                                    </>
                                  )}
                                  {shipLocSelectedCoverage.coveragePct >= 30 && shipLocSelectedCoverage.coveragePct < 90 && (
                                    <>
                                      {' '}Consider consolidating stock here before you turn on live carrier shipping.
                                    </>
                                  )}
                                </span>
                              </div>
                            )}
                            {shipLocTotalTracked === 0 && (
                              <div className="ship-loc-preview-coverage ship-loc-tone-neutral">
                                <span className="ship-loc-coverage-txt">
                                  You don't have any physical products yet — coverage will show up once you add them.
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {shipLocList.length > 0 && (
                    <div className="st-card-foot">
                      <button
                        type="button"
                        className="st-btn"
                        onClick={() => setShipLocSelectedId(store?.defaultShippingLocationId || '')}
                        disabled={!shipLocDirty || shipLocSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="st-btn st-btn-primary"
                        onClick={saveDefaultShippingLocation}
                        disabled={!shipLocDirty || shipLocSaving}
                      >
                        {shipLocSaving ? (
                          <>
                            <RefreshCw size={14} aria-hidden="true" className="ship-loc-spin" />
                            <span>Saving…</span>
                          </>
                        ) : (
                          <>
                            <Check size={14} aria-hidden="true" />
                            <span>Save changes</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </section>

                {/* Customer pickup — configured per active store location. */}
                <section className="st-card pickup-settings-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Customer pickup</h2>
                      <p className="st-card-sub">Let customers collect paid physical orders from selected locations. Each location can have its own collection instructions and preparation time.</p>
                    </div>

                    {shipLocTotalTracked === 0 ? (
                      <div className="ship-loc-empty">
                        <div className="ship-loc-empty-icon" aria-hidden="true"><Package size={20} /></div>
                        <div className="ship-loc-empty-body">
                          <div className="ship-loc-empty-title">Pickup is for physical products</div>
                          <p className="ship-loc-empty-sub">Add a physical product before offering collection at a store location.</p>
                        </div>
                      </div>
                    ) : pickupFeatureLoading ? (
                      <div className="ship-loc-skeleton" aria-hidden="true"><div className="ship-loc-skeleton-row" /></div>
                    ) : !hasPickupAccess ? (
                      <div className="ship-loc-empty">
                        <div className="ship-loc-empty-icon" aria-hidden="true"><MapPin size={20} /></div>
                        <div className="ship-loc-empty-body">
                          <div className="ship-loc-empty-title">Customer pickup is available on Pro</div>
                          <p className="ship-loc-empty-sub">Upgrade to let customers collect online orders from your store.</p>
                          <button type="button" className="st-btn st-btn-primary" onClick={() => navigateToUpgrade(navigate)}>View plans</button>
                        </div>
                      </div>
                    ) : shipLocList.length === 0 ? (
                      <div className="ship-loc-empty">
                        <div className="ship-loc-empty-icon" aria-hidden="true"><MapPin size={20} /></div>
                        <div className="ship-loc-empty-body">
                          <div className="ship-loc-empty-title">Add a location before enabling pickup</div>
                          <p className="ship-loc-empty-sub">Customers need a clear collection address.</p>
                          <button type="button" className="st-btn st-btn-primary" onClick={() => navigate('/locations')}>Add a location</button>
                        </div>
                      </div>
                    ) : (
                      <div className="pickup-location-list">
                        {shipLocList.map((location) => {
                          const draft = pickupDrafts[location.id];
                          if (!draft) return null;
                          const addressText = [location.address, location.city, location.state].filter(Boolean).join(', ');
                          const prep = draft.pickupPrepMinutes;
                          const dirty = isPickupDraftDirty(location.id);
                          const saving = pickupSavingId === location.id;
                          const instructionsLen = (draft.pickupInstructions || '').length;
                          return (
                            <div
                              className={`pickup-loc-card${draft.offersPickup ? ' pickup-loc-card--active' : ''}`}
                              key={location.id}
                            >
                              <div className="pickup-loc-card__header">
                                <div className="pickup-loc-card__icon" aria-hidden>
                                  <MapPin size={18} />
                                </div>
                                <div className="pickup-loc-card__identity">
                                  <div className="pickup-loc-card__name">{location.name}</div>
                                  <div className="pickup-loc-card__address">
                                    {addressText || 'No address on file for this location'}
                                  </div>
                                </div>
                                <label className="st-toggle pickup-loc-card__toggle">
                                  <input
                                    type="checkbox"
                                    checked={draft.offersPickup}
                                    onChange={(e) => updatePickupDraft(location.id, 'offersPickup', e.target.checked)}
                                  />
                                  <span className="st-toggle-track" />
                                </label>
                              </div>

                              {draft.offersPickup && (
                                <div className="pickup-loc-card__body">
                                  <div className="pickup-loc-card__fields">
                                    <div className="st-field pickup-loc-card__prep">
                                      <label className="st-field-label">
                                        <Clock size={12} aria-hidden style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                                        Ready in (minutes)
                                      </label>
                                      <div className="pickup-loc-card__stepper">
                                        <button
                                          type="button"
                                          className="pickup-loc-card__stepper-btn"
                                          onClick={() => updatePickupDraft(location.id, 'pickupPrepMinutes', Math.max(0, Number(prep || 0) - 5))}
                                          aria-label="Decrease prep time"
                                        >
                                          −
                                        </button>
                                        <input
                                          className="st-input pickup-loc-card__stepper-input"
                                          type="number"
                                          min="0"
                                          value={draft.pickupPrepMinutes ?? ''}
                                          onChange={(e) => updatePickupDraft(location.id, 'pickupPrepMinutes', e.target.value === '' ? null : Number(e.target.value))}
                                        />
                                        <button
                                          type="button"
                                          className="pickup-loc-card__stepper-btn"
                                          onClick={() => updatePickupDraft(location.id, 'pickupPrepMinutes', Number(prep || 0) + 5)}
                                          aria-label="Increase prep time"
                                        >
                                          +
                                        </button>
                                      </div>
                                      <div className="st-field-hint">Shown to customers at checkout as "Ready in ~{Number.isFinite(prep) ? prep : 30} min".</div>
                                    </div>

                                    <div className="st-field pickup-loc-card__instructions">
                                      <label className="st-field-label">Collection instructions (optional)</label>
                                      <textarea
                                        className="st-textarea"
                                        rows={2}
                                        maxLength={240}
                                        value={draft.pickupInstructions}
                                        onChange={(e) => updatePickupDraft(location.id, 'pickupInstructions', e.target.value)}
                                        placeholder="e.g. Ask for Ada at the counter, or use the side entrance"
                                      />
                                      <div className="st-field-hint pickup-loc-card__counter">
                                        <span>{instructionsLen}/240</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="pickup-loc-card__preview">
                                    <div className="pickup-loc-card__preview-label">Customer preview</div>
                                    <div className="pickup-loc-card__preview-row">
                                      <MapPin size={14} aria-hidden />
                                      <div className="pickup-loc-card__preview-text">
                                        <strong>{location.name}</strong>
                                        <span>
                                          {Number.isFinite(prep) ? `Ready in ~${prep} min` : 'Collection details sent after payment'}
                                          {draft.pickupInstructions ? ` · ${draft.pickupInstructions}` : ''}
                                        </span>
                                      </div>
                                      <span className="pickup-loc-card__preview-free">FREE</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {dirty && (
                                <div className="pickup-loc-card__footer">
                                  <button
                                    type="button"
                                    className="st-btn st-btn-ghost"
                                    onClick={() => resetPickupDraft(location.id)}
                                    disabled={saving}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="st-btn st-btn-primary"
                                    onClick={() => savePickupSettings(location.id)}
                                    disabled={saving}
                                  >
                                    {saving ? (
                                      <>
                                        <RefreshCw size={14} aria-hidden className="ship-loc-spin" />
                                        <span>Saving…</span>
                                      </>
                                    ) : (
                                      <>
                                        <Check size={14} aria-hidden />
                                        <span>Save changes</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>

                {/* Shipping zones — flat-rate rules per region */}
                <section className="st-card">
                  <div className="st-card-body">
                    <div className="st-card-head">
                      <h2 className="st-card-title">Shipping zones</h2>
                      <p className="st-card-sub">Set per-state shipping costs, delivery times, and free-shipping thresholds. Managed on a dedicated page.</p>
                    </div>

                    <div className="st-card-foot" style={{ justifyContent: 'flex-start', borderTop: 'none', paddingTop: 0 }}>
                      <button
                        type="button"
                        className="st-btn st-btn-primary"
                        onClick={() => navigate('/shipping-zones')}
                      >
                        <Truck size={14} aria-hidden="true" />
                        <span>Manage shipping zones</span>
                      </button>
                    </div>
                  </div>
                </section>
              </>
            )}

            {activeTab === 'integrations' && (
              <section className="st-card">
                <div className="st-card-body">
                  <div className="st-card-head">
                    <h2 className="st-card-title">Integrations</h2>
                    <p className="st-card-sub">Connect your store with third-party services to extend functionality.</p>
                  </div>

                  <div className="st-int-grid">
                    {/* Gmail — always available */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('gmail')}>
                      <span className="st-int-icon st-int-icon-gmail" aria-hidden="true"><BrandIcon.Gmail /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">Gmail</h3>
                        </div>
                        <p className="st-int-desc">Send customer emails from your own Gmail for better deliverability.</p>
                        <span className={`st-int-status ${gmailStatus.connected ? 'st-int-status-on' : 'st-int-status-off'}`}>
                          {gmailStatus.loading ? 'Loading…' : gmailStatus.connected ? 'Connected' : 'Not connected'}
                        </span>
                      </div>
                    </button>

                    {/* Google Calendar — always available */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('calendar')}>
                      <span className="st-int-icon st-int-icon-calendar" aria-hidden="true"><BrandIcon.Calendar /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">Google Calendar</h3>
                        </div>
                        <p className="st-int-desc">Sync appointments and bookings with your Google Calendar.</p>
                        <span className={`st-int-status ${calendarStatus.connected ? 'st-int-status-on' : 'st-int-status-off'}`}>
                          {calendarStatus.loading ? 'Loading…' : calendarStatus.connected ? 'Connected' : 'Not connected'}
                        </span>
                      </div>
                    </button>

                    {/* Google Sheets — PRO PLUS / PREMIUM */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('sheets')}>
                      <span className="st-int-icon st-int-icon-sheets" aria-hidden="true"><BrandIcon.Sheets /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">Google Sheets</h3>
                          {!hasSheetsAccess && <span className="st-int-badge">Pro</span>}
                        </div>
                        <p className="st-int-desc">Export customers and orders to Google Sheets for analysis.</p>
                        <span className={`st-int-status ${sheetsStatus.connected ? 'st-int-status-on' : 'st-int-status-off'}`}>
                          {sheetsStatus.loading ? 'Loading…' : sheetsStatus.connected ? 'Connected' : 'Not connected'}
                        </span>
                      </div>
                    </button>

                    {/* WhatsApp Orders — PRO / PRO PLUS / PREMIUM */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('whatsapp')}>
                      <span className="st-int-icon st-int-icon-whatsapp" aria-hidden="true"><BrandIcon.WhatsApp /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">WhatsApp Orders</h3>
                          {!hasWhatsAppAccess && !whatsAppFeatureLoading && <span className="st-int-badge">Pro</span>}
                        </div>
                        <p className="st-int-desc">Accept and manage orders directly through WhatsApp.</p>
                        <span className={`st-int-status ${store?.whatsappOrdersEnabled ? 'st-int-status-on' : 'st-int-status-off'}`}>
                          {store?.whatsappOrdersEnabled ? 'Enabled' : 'Not enabled'}
                        </span>
                      </div>
                    </button>

                    {/* Terminal Africa — nationwide multi-carrier parcel */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('terminal_africa')}>
                      <span className="st-int-icon st-int-icon-terminal" aria-hidden="true"><Package /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">Nationwide Shipping</h3>
                          {!hasTerminalAccess && !terminalFeatureLoading && <span className="st-int-badge">Pro</span>}
                        </div>
                        <p className="st-int-desc">Show live rates from GIG, DHL, Fez, Kwik and more at checkout — powered by Terminal Africa.</p>
                        <span className={`st-int-status ${
                          terminalEnabled
                            ? 'st-int-status-on'
                            : terminalPreconditions.walletSufficient === false
                              ? 'st-int-status-warn'
                              : 'st-int-status-off'
                        }`}>
                          {terminalLoading
                            ? 'Loading…'
                            : terminalEnabled
                              ? 'Enabled'
                              : terminalPreconditions.walletSufficient === false
                                ? `Top up wallet to ₦${TERMINAL_MIN_BALANCE.toLocaleString()}`
                                : 'Not enabled'}
                        </span>
                      </div>
                    </button>

                    {/* Chowdeck Delivery — feature open to all tiers + requires ₦5,000 wallet */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('chowdeck')}>
                      <span className="st-int-icon st-int-icon-chowdeck" aria-hidden="true"><Bike /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">Chowdeck Delivery</h3>
                          {!hasChowdeckAccess && !chowdeckFeatureLoading && <span className="st-int-badge">Pro</span>}
                        </div>
                        <p className="st-int-desc">Offer express delivery to customers via Chowdeck.</p>
                        <span className={`st-int-status ${chowdeckSettings.enabled ? 'st-int-status-on' : !chowdeckMeetsMinimum ? 'st-int-status-warn' : 'st-int-status-off'}`}>
                          {chowdeckLoading
                            ? 'Loading…'
                            : chowdeckSettings.enabled
                              ? 'Enabled'
                              : !chowdeckMeetsMinimum
                                ? `Top up wallet to ₦${CHOWDECK_MIN_BALANCE.toLocaleString()}`
                                : 'Not enabled'}
                        </span>
                      </div>
                    </button>

                    {/* WooCommerce — PRO / PRO PLUS / PREMIUM */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('woocommerce')}>
                      <span className="st-int-icon st-int-icon-woo" aria-hidden="true"><BrandIcon.WooCommerce /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">WooCommerce</h3>
                          {!hasWooCommerceAccess && <span className="st-int-badge">Pro</span>}
                        </div>
                        <p className="st-int-desc">Import products, orders & customers from your WooCommerce store.</p>
                        <span className={`st-int-status ${wooStatus.connected ? 'st-int-status-on' : 'st-int-status-off'}`}>
                          {wooStatus.loading ? 'Loading…' : wooStatus.connected ? 'Connected' : 'Not connected'}
                        </span>
                      </div>
                    </button>

                    {/* Google Analytics 4 — PRO / PRO PLUS */}
                    <button type="button" className="st-int-card" onClick={() => setActiveIntegrationModal('ga4')}>
                      <span className="st-int-icon st-int-icon-ga4" aria-hidden="true"><BarChart3 /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">Google Analytics</h3>
                          {!hasGA4Access && <span className="st-int-badge">Pro</span>}
                        </div>
                        <p className="st-int-desc">Track customer behavior and e-commerce events with GA4.</p>
                        <span className={`st-int-status ${ga4Status.enabled ? 'st-int-status-on' : 'st-int-status-off'}`}>
                          {ga4Status.loading ? 'Loading…' : ga4Status.enabled ? 'Enabled' : 'Not enabled'}
                        </span>
                      </div>
                    </button>

                    {/* API Access — Coming Soon (no modal trigger) */}
                    <div className="st-int-card is-disabled" aria-disabled="true">
                      <span className="st-int-icon st-int-icon-api" aria-hidden="true"><Code2 /></span>
                      <div className="st-int-body">
                        <div className="st-int-head">
                          <h3 className="st-int-name">API Access</h3>
                        </div>
                        <p className="st-int-desc">Build custom integrations with your store's public API.</p>
                        <span className="st-int-status st-int-status-soon">Coming soon</span>
                      </div>
                    </div>
                  </div>

                  {/* Gmail Modal */}
                  {activeIntegrationModal === 'gmail' && (
                    <div className="int-modal-overlay" onClick={() => setActiveIntegrationModal(null)}>
                      <div className="int-modal" onClick={e => e.stopPropagation()}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <div className="int-modal-icon int-icon-gmail">
                              <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                            </div>
                            <h2 className="int-modal-title">Gmail Integration</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => setActiveIntegrationModal(null)}>×</button>
                        </div>
                        <div className="int-modal-body">
                          <div className="int-modal-section">
                            <p className="int-modal-desc">
                              Connect your Gmail account to send customer order notifications, campaigns, and receipts from your own email address instead of the platform email.
                            </p>
                          </div>
                          {gmailStatus.connected && (
                            <div className="int-modal-section">
                              <h4>Connection Status</h4>
                              <div className="int-connected-box int-connected-box-success">
                                <p className="int-connected-label">Connected as</p>
                                <p className="int-connected-value">{gmailStatus.email}</p>
                              </div>
                            </div>
                          )}
                          <div className="int-modal-section">
                            <h4>Features</h4>
                            <div className="int-features-list">
                              <div className="int-feature-item">
                                <div className="int-feature-icon">✓</div>
                                <div className="int-feature-text">
                                  <h5>Order Notifications</h5>
                                  <p>Send from your email address</p>
                                </div>
                              </div>
                              <div className="int-feature-item">
                                <div className="int-feature-icon">✓</div>
                                <div className="int-feature-text">
                                  <h5>Campaign Emails</h5>
                                  <p>Better deliverability & brand recognition</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="int-modal-actions">
                          {gmailStatus.connected ? (
                            <>
                              <button className="int-modal-btn int-modal-btn-secondary" onClick={sendTestEmail} disabled={gmailTestingEmail}>
                                {gmailTestingEmail ? 'Sending...' : 'Send Test Email'}
                              </button>
                              <button className="int-modal-btn int-modal-btn-danger" onClick={disconnectGmail}>
                                Disconnect
                              </button>
                            </>
                          ) : (
                            <button className="int-modal-btn int-modal-btn-primary" onClick={connectGmail} disabled={gmailConnecting}>
                              {gmailConnecting ? 'Connecting...' : 'Connect Gmail'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Calendar Modal */}
                  {activeIntegrationModal === 'calendar' && (
                    <div className="int-modal-overlay" onClick={() => setActiveIntegrationModal(null)}>
                      <div className="int-modal" onClick={e => e.stopPropagation()}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <div className="int-modal-icon int-icon-calendar">
                              <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/></svg>
                            </div>
                            <h2 className="int-modal-title">Google Calendar</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => setActiveIntegrationModal(null)}>×</button>
                        </div>
                        <div className="int-modal-body">
                          <div className="int-modal-section">
                            <p className="int-modal-desc">
                              Sync your appointment bookings with Google Calendar. When customers book appointments, they'll automatically appear in your calendar.
                            </p>
                          </div>
                          {calendarStatus.connected && (
                            <div className="int-modal-section">
                              <h4>Connection Status</h4>
                              <div className="int-connected-box int-connected-box-success">
                                <p className="int-connected-label">Connected as</p>
                                <p className="int-connected-value">{calendarStatus.email}</p>
                              </div>
                            </div>
                          )}
                          <div className="int-modal-section">
                            <h4>Features</h4>
                            <div className="int-features-list">
                              <div className="int-feature-item">
                                <div className="int-feature-icon">✓</div>
                                <div className="int-feature-text">
                                  <h5>Auto-Sync Bookings</h5>
                                  <p>Appointments sync to your calendar</p>
                                </div>
                              </div>
                              <div className="int-feature-item">
                                <div className="int-feature-icon">✓</div>
                                <div className="int-feature-text">
                                  <h5>Availability Check</h5>
                                  <p>Prevent double-booking</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="int-modal-actions">
                          {calendarStatus.connected ? (
                            <button className="int-modal-btn int-modal-btn-danger" onClick={disconnectCalendar}>
                              Disconnect
                            </button>
                          ) : (
                            <button className="int-modal-btn int-modal-btn-primary" onClick={connectCalendar} disabled={calendarConnecting}>
                              {calendarConnecting ? 'Connecting...' : 'Connect Google Calendar'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sheets Modal */}
                  {activeIntegrationModal === 'sheets' && (
                    <div className="int-modal-overlay" onClick={() => setActiveIntegrationModal(null)}>
                      <div className="int-modal" onClick={e => e.stopPropagation()}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <div className="int-modal-icon int-icon-sheets">
                              <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/></svg>
                            </div>
                            <h2 className="int-modal-title">Google Sheets</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => setActiveIntegrationModal(null)}>×</button>
                        </div>
                        <div className="int-modal-body">
                          <div className="int-modal-section">
                            <p className="int-modal-desc">
                              Export your customers and orders to Google Sheets for analysis, reporting, or backup purposes.
                            </p>
                          </div>
                          {sheetsStatus.connected ? (
                            <>
                              <div className="int-modal-section">
                                <h4>Connection Status</h4>
                                <div className="int-connected-box int-connected-box-success">
                                  <p className="int-connected-label">Connected as</p>
                                  <p className="int-connected-value">{sheetsStatus.email}</p>
                                </div>
                              </div>
                              <div className="int-modal-section">
                                <h4>Export Data</h4>
                                <div className="int-export-options">
                                  <button className="int-export-btn" onClick={exportCustomersToSheets} disabled={sheetsExporting}>
                                    <div className="int-export-btn-icon" style={{ background: '#D1FAE5', color: '#059669' }}>👥</div>
                                    <div className="int-export-btn-text">
                                      <h5>{sheetsExporting === 'customers' ? 'Exporting...' : 'Export Customers'}</h5>
                                      <p>Name, email, phone, order history</p>
                                    </div>
                                  </button>
                                  <button className="int-export-btn" onClick={exportOrdersToSheets} disabled={sheetsExporting}>
                                    <div className="int-export-btn-icon" style={{ background: '#DBEAFE', color: '#2563EB' }}>📦</div>
                                    <div className="int-export-btn-text">
                                      <h5>{sheetsExporting === 'orders' ? 'Exporting...' : 'Export Orders'}</h5>
                                      <p>Order details, items, totals</p>
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="int-modal-section">
                              <h4>Features</h4>
                              <div className="int-features-list">
                                <div className="int-feature-item">
                                  <div className="int-feature-icon">✓</div>
                                  <div className="int-feature-text">
                                    <h5>Export Customers</h5>
                                    <p>Customer data to Google Sheets</p>
                                  </div>
                                </div>
                                <div className="int-feature-item">
                                  <div className="int-feature-icon">✓</div>
                                  <div className="int-feature-text">
                                    <h5>Export Orders</h5>
                                    <p>Order history with details</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="int-modal-actions">
                          {sheetsStatus.connected ? (
                            <button className="int-modal-btn int-modal-btn-danger" onClick={disconnectSheets}>
                              Disconnect
                            </button>
                          ) : (
                            <button className="int-modal-btn int-modal-btn-primary" onClick={connectSheets} disabled={sheetsConnecting}>
                              {sheetsConnecting ? 'Connecting...' : 'Connect Google Sheets'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* WhatsApp Modal */}
                  {activeIntegrationModal === 'whatsapp' && (
                    <div className="int-modal-overlay" onClick={() => setActiveIntegrationModal(null)}>
                      <div className="int-modal" onClick={e => e.stopPropagation()}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <div className="int-modal-icon int-icon-whatsapp">
                              <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            </div>
                            <h2 className="int-modal-title">WhatsApp Orders</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => setActiveIntegrationModal(null)}>×</button>
                        </div>
                        <div className="int-modal-body">
                          <div className="int-modal-section">
                            <p className="int-modal-desc">
                              Accept and manage orders directly through WhatsApp. Customers can browse your catalog and place orders via WhatsApp.
                            </p>
                          </div>
                          <div className="int-toggle-row">
                            <span className="int-toggle-label">Enable WhatsApp Orders</span>
                            <label className="int-toggle">
                              <input
                                type="checkbox"
                                checked={store?.whatsappOrdersEnabled || false}
                                onChange={async (e) => {
                                  const enabled = e.target.checked;
                                  if (enabled && !hasWhatsAppAccess) {
                                    e.target.checked = false;
                                    navigateToUpgrade(navigate);
                                    return;
                                  }
                                  try {
                                    const axios = getAxios();
                                    await axios.post('/stores', { whatsappOrdersEnabled: enabled });
                                    dispatch(fetchMyStore(axios));
                                    toast.success(enabled ? 'WhatsApp Orders enabled' : 'WhatsApp Orders disabled');
                                  } catch (error) {
                                    console.error('Error updating WhatsApp orders:', error);
                                    toast.error('Failed to update WhatsApp Orders setting');
                                  }
                                }}
                              />
                              <span className="int-toggle-slider"></span>
                            </label>
                          </div>
                          {!hasWhatsAppAccess && (
                            <div style={{ padding: '12px', background: '#FEF3C7', borderRadius: '8px', marginTop: '12px' }}>
                              <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
                                WhatsApp Orders requires a Pro plan or higher. <button onClick={() => navigateToUpgrade(navigate)} style={{ color: '#D97706', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Upgrade now</button>
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="int-modal-actions">
                          <button className="int-modal-btn int-modal-btn-secondary" onClick={() => setActiveIntegrationModal(null)}>
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Chowdeck Modal */}
                  {activeIntegrationModal === 'chowdeck' && (
                    <div className="int-modal-overlay" onClick={() => setActiveIntegrationModal(null)}>
                      <div className="int-modal" onClick={e => e.stopPropagation()}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <div className="int-modal-icon int-icon-chowdeck">
                              <span style={{ fontSize: '20px' }}>🚴</span>
                            </div>
                            <h2 className="int-modal-title">Chowdeck Delivery</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => setActiveIntegrationModal(null)}>×</button>
                        </div>
                        <div className="int-modal-body">
                          <div className="int-modal-section">
                            <p className="int-modal-desc">
                              Offer Chowdeck express delivery to customers at checkout for fast same-day delivery in supported cities.
                            </p>
                          </div>
                          {chowdeckPlatformConfigured ? (
                            <>
                              <div className="int-toggle-row">
                                <span className="int-toggle-label">Enable Chowdeck Delivery</span>
                                <label className="int-toggle">
                                  <input
                                    type="checkbox"
                                    checked={chowdeckSettings.enabled || false}
                                    disabled={!hasChowdeckAccess}
                                    onChange={async (e) => {
                                      const enabled = e.target.checked;
                                      if (enabled && !hasChowdeckAccess) {
                                        e.target.checked = false;
                                        navigateToUpgrade(navigate);
                                        return;
                                      }
                                      if (enabled) {
                                        setShowChowdeckForm(true);
                                      } else {
                                        saveChowdeckSettings({ ...chowdeckSettings, enabled: false });
                                      }
                                    }}
                                  />
                                  <span className="int-toggle-slider"></span>
                                </label>
                              </div>
                              {!hasChowdeckAccess && (
                                <div style={{ padding: '12px', background: '#FEF3C7', borderRadius: '8px', marginTop: '12px' }}>
                                  <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
                                    Chowdeck Delivery requires a Pro plan or higher. <button onClick={() => navigateToUpgrade(navigate)} style={{ color: '#D97706', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Upgrade now</button>
                                  </p>
                                </div>
                              )}
                              {chowdeckSettings.enabled && (
                                <div style={{ marginTop: '16px', padding: '12px', background: '#FFF7ED', borderRadius: '8px', border: '1px solid #FDBA74' }}>
                                  <p style={{ fontSize: '13px', color: '#9A3412', margin: 0 }}>
                                    <strong>Shipping origin:</strong> {chowdeckOriginLabel || 'Not configured'}<br/>
                                    <strong>Contact:</strong> {chowdeckSettings.pickupContact?.name} ({chowdeckSettings.pickupContact?.phone})
                                  </p>
                                  <button onClick={() => setShowChowdeckForm(true)} style={{ marginTop: '8px', padding: '6px 12px', fontSize: '12px', background: 'white', border: '1px solid #FDBA74', borderRadius: '6px', cursor: 'pointer', color: '#9A3412' }}>
                                    Edit courier contact
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ padding: '12px', background: '#F3F4F6', borderRadius: '8px' }}>
                              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                                Chowdeck integration is not yet configured by the platform administrator.
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="int-modal-actions">
                          <button className="int-modal-btn int-modal-btn-secondary" onClick={() => setActiveIntegrationModal(null)}>
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Chowdeck Configuration Form Modal */}
                  {showChowdeckForm && (
                    <div className="int-modal-overlay" onClick={() => setShowChowdeckForm(false)}>
                      <div className="int-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <h2 className="int-modal-title">Configure Chowdeck delivery</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => setShowChowdeckForm(false)}>×</button>
                        </div>
                        <div className="int-modal-body">
                          <div style={{ display: 'grid', gap: '16px' }}>
                            <div style={{ padding: '12px', background: hasChowdeckOrigin ? '#F0FDF4' : '#FFF7ED', border: `1px solid ${hasChowdeckOrigin ? '#BBF7D0' : '#FDBA74'}`, borderRadius: '8px' }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: hasChowdeckOrigin ? '#166534' : '#9A3412' }}>
                                Shipping origin
                              </div>
                              <p style={{ fontSize: '13px', color: hasChowdeckOrigin ? '#166534' : '#9A3412', margin: '4px 0 0' }}>
                                {hasChowdeckOrigin
                                  ? chowdeckOriginLabel
                                  : 'Choose a shipping & fulfillment origin before enabling Chowdeck.'}
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowChowdeckForm(false);
                                  setActiveIntegrationModal(null);
                                  setActiveTab('shipping');
                                }}
                                style={{ marginTop: '10px', padding: '6px 12px', fontSize: '12px', background: 'white', border: `1px solid ${hasChowdeckOrigin ? '#86EFAC' : '#FDBA74'}`, borderRadius: '6px', cursor: 'pointer', color: hasChowdeckOrigin ? '#166534' : '#9A3412' }}
                              >
                                Manage shipping origin
                              </button>
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Contact Name *</label>
                              <input type="text" value={chowdeckSettings.pickupContact?.name || ''} onChange={e => setChowdeckSettings({...chowdeckSettings, pickupContact: {...chowdeckSettings.pickupContact, name: e.target.value}})} placeholder="Contact person name" style={{ width: '100%', padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Contact Phone *</label>
                              <input type="tel" value={chowdeckSettings.pickupContact?.phone || ''} onChange={e => setChowdeckSettings({...chowdeckSettings, pickupContact: {...chowdeckSettings.pickupContact, phone: e.target.value}})} placeholder="Phone number" style={{ width: '100%', padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px' }} />
                            </div>
                          </div>
                        </div>
                        <div className="int-modal-actions">
                          <button className="int-modal-btn int-modal-btn-secondary" onClick={() => setShowChowdeckForm(false)}>Cancel</button>
                          <button className="int-modal-btn int-modal-btn-primary" onClick={() => saveChowdeckSettings({...chowdeckSettings, enabled: true})} disabled={chowdeckSaving || !hasChowdeckOrigin}>
                            {chowdeckSaving ? 'Saving...' : 'Save & Enable'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Terminal Africa (Nationwide Shipping) Modal */}
                  {activeIntegrationModal === 'terminal_africa' && (
                    <div className="int-modal-overlay" onClick={() => setActiveIntegrationModal(null)}>
                      <div className="int-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <div className="int-modal-icon int-icon-terminal">
                              <Package />
                            </div>
                            <h2 className="int-modal-title">Nationwide Shipping</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => setActiveIntegrationModal(null)}>×</button>
                        </div>

                        <div className="int-modal-body">
                          {terminalLoading ? (
                            <div className="ship-loc-skeleton" aria-hidden="true">
                              <div className="ship-loc-skeleton-row" />
                              <div className="ship-loc-skeleton-row" style={{ width: '70%' }} />
                            </div>
                          ) : !hasTerminalAccess ? (
                            <div className="ta-locked">
                              <p>Nationwide multi-carrier shipping is a <strong>Pro</strong> feature.</p>
                              <p className="st-field-hint">Upgrade your plan to enable live rates from GIG, DHL, Fez, Kwik, and more at checkout.</p>
                            </div>
                          ) : !terminalPreconditions.platformConfigured ? (
                            <div className="ta-notice ta-notice-warn">
                              <AlertTriangle size={16} aria-hidden="true" />
                              <span>Terminal Africa isn't configured on the platform yet. Please check back later.</span>
                            </div>
                          ) : (
                            <>
                              <p className="int-modal-desc">
                                Offer real-time rates from 13+ Nigerian and international couriers at checkout.
                                Customers pick their preferred carrier and pay for shipping upfront.
                              </p>

                              {/* Precondition checklist — merchant sees exactly what's blocking */}
                              <div className="ta-checklist">
                                <PreconditionRow ok={terminalPreconditions.kycApproved} label="KYC verification approved" />
                                <PreconditionRow ok={terminalPreconditions.hasDefaultShippingLocation} label="Default shipping location set" hint="Under Settings → Shipping" />
                                <PreconditionRow
                                  ok={terminalPreconditions.walletSufficient}
                                  label={`Wallet balance ≥ ₦${TERMINAL_MIN_BALANCE.toLocaleString()}`}
                                  hint={terminalData?.wallet?.availableBalance != null
                                    ? `Current: ₦${Number(terminalData.wallet.availableBalance).toLocaleString()}`
                                    : null}
                                />
                              </div>

                              {/* Enable toggle + TOS acceptance — pill-slider matches other integrations */}
                              <label className="int-toggle-row">
                                <span className="int-toggle-label">
                                  {terminalEnabled ? 'Enabled' : 'Enable nationwide shipping'}
                                </span>
                                <span className="int-toggle">
                                  <input
                                    type="checkbox"
                                    checked={terminalEnabled}
                                    onChange={(e) => {
                                      const needsTos = !terminalData?.settings?.tosAcceptedAt
                                        || terminalData?.settings?.tosVersion !== terminalData?.currentTosVersion;
                                      if (e.target.checked && needsTos && !terminalTosChecked) {
                                        toast.warning('Accept the terms below before enabling.');
                                        return;
                                      }
                                      saveTerminalSettings({ enabled: e.target.checked, acceptTos: needsTos && terminalTosChecked });
                                    }}
                                    disabled={terminalSaving || (!terminalCanEnable && !terminalEnabled)}
                                  />
                                  <span className="int-toggle-slider" />
                                </span>
                              </label>

                              {!terminalEnabled && (!terminalData?.settings?.tosAcceptedAt || terminalData?.settings?.tosVersion !== terminalData?.currentTosVersion) && (
                                <label className="ta-tos-row">
                                  <input
                                    type="checkbox"
                                    checked={terminalTosChecked}
                                    onChange={(e) => setTerminalTosChecked(e.target.checked)}
                                    disabled={terminalSaving}
                                  />
                                  <span>
                                    I understand that Revstar transacts with courier partners on my behalf using our shared platform account.
                                    I agree not to ship prohibited items (firearms, drugs, hazmat, live animals, counterfeit goods, etc.).
                                    Revstar may suspend my shipping if I breach these terms.
                                  </span>
                                </label>
                              )}

                              {terminalEnabled && terminalData?.settings?.tosAcceptedAt && (
                                <p className="st-field-hint" style={{ marginTop: 12 }}>
                                  Terms accepted {new Date(terminalData.settings.tosAcceptedAt).toLocaleDateString()} (v{terminalData.settings.tosVersion}).
                                </p>
                              )}

                              {terminalEnabled && terminalData?.settings?.dailyShipmentCap != null && (
                                <div className="ta-notice ta-notice-info">
                                  <span>
                                    Your plan allows up to <strong>{terminalData.settings.dailyShipmentCap.toLocaleString()}</strong> shipments per day.
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div className="int-modal-actions">
                          <button type="button" className="int-modal-btn int-modal-btn-secondary" onClick={() => setActiveIntegrationModal(null)}>Close</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* WooCommerce Integration Modal */}
                  <WooCommerceIntegration
                    isOpen={activeIntegrationModal === 'woocommerce'}
                    onClose={() => setActiveIntegrationModal(null)}
                    toast={toast}
                    onStatusChange={() => {
                      // Refresh WooCommerce status
                      fetch('/api/merchant/woocommerce/status', { credentials: 'include' })
                        .then(r => r.json())
                        .then(data => {
                          setWooStatus({
                            connected: data.connected || false,
                            storeName: data.integration?.storeName || null,
                            storeUrl: data.integration?.storeUrl || null,
                            loading: false
                          });
                        })
                        .catch(() => {});
                    }}
                  />

                  {/* GA4 Integration Modal */}
                  {activeIntegrationModal === 'ga4' && (
                    <div className="int-modal-overlay" onClick={() => setActiveIntegrationModal(null)}>
                      <div className="int-modal int-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="int-modal-header">
                          <div className="int-modal-header-left">
                            <div className="int-modal-icon" style={{ background: 'linear-gradient(135deg, #F9AB00, #E37400)' }}>
                              <svg viewBox="0 0 24 24" fill="none" width="24" height="24">
                                <path d="M3 3v18h18M7 16l4-4 4 4 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <h2 className="int-modal-title">Google Analytics 4</h2>
                          </div>
                          <button className="int-modal-close" onClick={() => {
                            setActiveIntegrationModal(null);
                            // Refresh GA4 status when closing
                            fetch('/api/ga4/merchant/config', { credentials: 'include' })
                              .then(r => r.json())
                              .then(data => {
                                setGa4Status({
                                  enabled: data.enabled || false,
                                  measurementId: data.measurementId || null,
                                  loading: false
                                });
                              })
                              .catch(() => {});
                          }}>×</button>
                        </div>
                        <div className="int-modal-body" style={{ padding: 0 }}>
                          <GA4Settings />
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </section>
            )}

            {activeTab === 'security' && (
              <section className="st-card">
                <div className="st-card-body">
                  <div className="st-card-head">
                    <h2 className="st-card-title">Security</h2>
                    <p className="st-card-sub">Keep your account secure. 2FA and session management are on the way.</p>
                  </div>

                  <div className="st-action-list">
                    <div className="st-action-row">
                      <span className="st-action-icon" aria-hidden="true"><Key size={18} /></span>
                      <div className="st-action-body">
                        <div className="st-action-head">
                          <h3 className="st-action-title">Change password</h3>
                        </div>
                        <p className="st-action-desc">Update your password regularly to keep your account secure.</p>
                      </div>
                      <div className="st-action-cta">
                        <button type="button" className="st-btn st-btn-secondary" onClick={() => setShowPasswordModal(true)}>
                          Change
                        </button>
                      </div>
                    </div>

                    <div className="st-action-row is-disabled">
                      <span className="st-action-icon" aria-hidden="true"><ShieldCheck size={18} /></span>
                      <div className="st-action-body">
                        <div className="st-action-head">
                          <h3 className="st-action-title">Two-factor authentication</h3>
                          <span className="st-int-badge st-int-badge-soon">Coming soon</span>
                        </div>
                        <p className="st-action-desc">Require a one-time code in addition to your password when you sign in.</p>
                      </div>
                      <div className="st-action-cta">
                        <button type="button" className="st-btn st-btn-secondary" disabled>
                          Enable
                        </button>
                      </div>
                    </div>

                    <div className="st-action-row is-disabled">
                      <span className="st-action-icon" aria-hidden="true"><MonitorSmartphone size={18} /></span>
                      <div className="st-action-body">
                        <div className="st-action-head">
                          <h3 className="st-action-title">Login sessions</h3>
                          <span className="st-int-badge st-int-badge-soon">Coming soon</span>
                        </div>
                        <p className="st-action-desc">See where you're signed in and sign out remote devices.</p>
                      </div>
                      <div className="st-action-cta">
                        <button type="button" className="st-btn st-btn-secondary" disabled>
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'payments' && (() => {
              // Mirrors backend gate at routes/merchantPaymentGateways.js:214 —
              // a LEVEL_0 merchant CAN still enable a gateway if they have
              // canReceivePayments set OR a KYC-verified bank on file.
              const bankVerifiedViaKyc = !!(kycStatus?.accountNumber && kycStatus?.accountVerified);
              const kycLocked = kycStatus
                && kycStatus.level === 'LEVEL_0'
                && !kycStatus.canReceivePayments
                && !bankVerifiedViaKyc;
              const bankAcct = kycStatus?.accountNumber;
              const bankLast4 = bankAcct ? String(bankAcct).slice(-4) : null;
              return (
              <div className="st-payments-layout">

                {/* KYC banner — once at the top */}
                {kycLocked && (
                  <div className="st-banner st-banner-warn st-banner-block">
                    <span className="st-banner-icon"><AlertTriangle size={18} /></span>
                    <div className="st-banner-body">
                      <p className="st-banner-title">KYC verification required</p>
                      <p className="st-banner-text">
                        Complete Level 1 verification to enable gateways, tax, and settlements.
                      </p>
                      <div className="st-banner-cta">
                        <button
                          type="button"
                          className="st-btn st-btn-primary"
                          onClick={() => { window.location.href = '/kyc'; }}
                        >
                          Complete KYC <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Group: Accept payments ─────────────────────────── */}
                <div className="st-payments-group-label">Accept payments</div>

                {/* Payment methods (gateways) */}
                <section className="st-card st-payment-methods st-payment-panel">
                  <div className="st-card-body is-tight">
                    <div className="st-card-head">
                      <div>
                        <h2 className="st-card-title">Payment methods</h2>
                        <p className="st-card-sub">Cards and bank-transfer rails available on your checkout.</p>
                      </div>
                    </div>
                    {loadingGateways ? (
                          <div className="st-loading">Loading…</div>
                        ) : paymentGateways.length === 0 ? (
                          <div className="st-empty">No gateways available. Contact support.</div>
                        ) : (
                          <>
                            {/* Grandfathered hint: backend stamps `grandfathered: true`
                                on every row for stores that already had >1 gateway
                                before we started enforcing multi-gateway gating.
                                Reading from any row is fine — the flag is store-level. */}
                            {paymentGateways.some(g => g.grandfathered) && (
                              <p className="st-list-hint">
                                Additional gateways stay active on your account. You can toggle these off any time, but adding new gateways below Pro is limited.
                              </p>
                            )}
                            <div className="st-gateway-list">
                              {paymentGateways.map(gateway => {
                                const isToggling = togglingGateway[gateway.name];
                                // Lock precedence, most restrictive first:
                                //   1. Advanced-feature (tier gate on 2nd+ gateway).
                                //      Backend field: canEnable === false with
                                //      lockedReason === 'advanced_feature'.
                                //   2. KYC (not reflected in GET response; local check).
                                // Backwards-compat: pre-Sprint-2 backend omits
                                // canEnable — treat undefined as `true` (allow).
                                const backendAllowsEnable = gateway.canEnable !== false;
                                const featureLocked = !gateway.enabled
                                  && gateway.lockedReason === 'advanced_feature';
                                const kycBlocked = kycLocked && !gateway.enabled && backendAllowsEnable;
                                const isLocked = featureLocked || kycBlocked;
                                return (
                                  <div
                                    key={gateway.id}
                                    className={`st-gateway-row${gateway.enabled ? ' is-active' : ''}`}
                                  >
                                    <div className="st-gateway-icon"><CreditCard size={18} /></div>
                                    <div className="st-gateway-body">
                                      <h4 className="st-gateway-name">{gateway.displayName || gateway.name}</h4>
                                      {gateway.enabled && (
                                        <span className="st-card-status">Active</span>
                                      )}
                                      {featureLocked && !gateway.enabled && (
                                        <span className="st-gateway-tier-pill">Pro</span>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className={`st-btn ${gateway.enabled ? 'st-btn-secondary' : 'st-btn-primary'}`}
                                      disabled={isToggling}
                                      onClick={async () => {
                                        if (featureLocked) {
                                          window.location.href = '/upgrade';
                                          return;
                                        }
                                        if (kycBlocked) {
                                          const confirmed = await confirm('Would you like to complete KYC verification now?', {
                                            title: 'KYC Verification Required',
                                            confirmText: 'Complete KYC',
                                            cancelText: 'Cancel',
                                            type: 'warning'
                                          });
                                          if (confirmed) window.location.href = '/kyc';
                                          return;
                                        }
                                        togglePaymentGateway(gateway.name, gateway.enabled);
                                      }}
                                    >
                                      {isLocked && <Lock size={14} />}
                                      {isToggling
                                        ? '…'
                                        : featureLocked
                                          ? 'Upgrade'
                                          : kycBlocked
                                            ? 'Locked'
                                            : gateway.enabled
                                              ? 'Disable'
                                              : 'Enable'}
                                    </button>
                                  </div>
                                );
                              })}

                              {/* Manual Bank Transfer row — locked reasons:
                                  advanced_feature (tier gate on payments.manual_transfer),
                                  vt_not_provisioned (Paystack Terminal not set up yet).
                                  Hidden entirely when the settings endpoint 404s
                                  (loadingManualTransfer stays false + no state
                                  changes) so older backends stay clean. */}
                              {!loadingManualTransfer && (manualTransfer.enabled || manualTransfer.canToggle || manualTransfer.lockedReason) && (() => {
                                const mtFeatureLocked = manualTransfer.lockedReason === 'advanced_feature';
                                const mtVtPending = manualTransfer.lockedReason === 'vt_not_provisioned';
                                const mtLocked = !manualTransfer.enabled && (mtFeatureLocked || mtVtPending);
                                return (
                                  <div className={`st-gateway-row${manualTransfer.enabled ? ' is-active' : ''}`}>
                                    <div className="st-gateway-icon"><Landmark size={18} /></div>
                                    <div className="st-gateway-body">
                                      <h4 className="st-gateway-name">Bank Transfer</h4>
                                      {manualTransfer.enabled && (
                                        <span className="st-card-status">Active</span>
                                      )}
                                      {mtFeatureLocked && !manualTransfer.enabled && (
                                        <span className="st-gateway-tier-pill">Pro</span>
                                      )}
                                      {mtVtPending && !manualTransfer.enabled && (
                                        <span className="st-gateway-tier-pill">Terminal pending</span>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className={`st-btn ${manualTransfer.enabled ? 'st-btn-secondary' : 'st-btn-primary'}`}
                                      disabled={togglingManualTransfer}
                                      onClick={() => {
                                        if (mtFeatureLocked) {
                                          window.location.href = '/upgrade';
                                          return;
                                        }
                                        if (mtVtPending) {
                                          toast.error('Your Paystack Terminal is still being provisioned. This usually completes after KYC verification.');
                                          return;
                                        }
                                        toggleManualTransfer();
                                      }}
                                    >
                                      {mtLocked && <Lock size={14} />}
                                      {togglingManualTransfer
                                        ? '…'
                                        : mtFeatureLocked
                                          ? 'Upgrade'
                                          : mtVtPending
                                            ? 'Pending'
                                            : manualTransfer.enabled
                                              ? 'Disable'
                                              : 'Enable'}
                                    </button>
                                  </div>
                                );
                              })()}

                              {/* COD always remains visible. It is a plan-controlled
                                  feature, not a hidden payment rail, so merchants can
                                  understand why it is unavailable and where to enable it. */}
                              {(() => {
                                const codReason = codSettings?.reason;
                                const codEnabled = codSettings?.enabled === true;
                                const codCanEnable = codSettings?.eligible === true && codReason !== 'ADMIN_SUSPENDED';
                                const codLocked = !loadingCodSettings && !codCanEnable;
                                const codAction = codEnabled ? 'Disable' : codCanEnable ? 'Enable' : 'Unavailable';
                                const codStatus = codEnabled
                                  ? 'Active'
                                  : codReason === 'PLATFORM_DISABLED'
                                    ? 'Coming soon'
                                    : codReason === 'SUBSCRIPTION_EXPIRED'
                                      ? 'Renew plan'
                                      : codReason === 'ADMIN_SUSPENDED'
                                        ? 'Paused'
                                        : codReason === 'PLAN_REQUIRED'
                                          ? 'Plan feature'
                                          : 'Unavailable';
                                return (
                                  <div className={`st-gateway-row st-cod-row${codEnabled ? ' is-active' : ''}`}>
                                    <div className="st-gateway-icon st-cod-icon"><Coins size={18} /></div>
                                    <div className="st-gateway-body st-cod-body">
                                      <div>
                                        <div className="st-cod-title-line">
                                          <h4 className="st-gateway-name">Cash on delivery</h4>
                                          <span className={codEnabled ? 'st-card-status' : 'st-cod-status'}>{loadingCodSettings ? 'Loading…' : codStatus}</span>
                                        </div>
                                        <p className="st-gateway-desc st-cod-desc">
                                          <strong>No service charge.</strong>
                                          <span>You collect payment after delivery.</span>
                                        </p>
                                        {(() => {
                                          if (codEnabled || loadingCodSettings) return null;
                                          const titleCase = (value) => String(value || '')
                                            .toLowerCase()
                                            .replace(/\b\w/g, (char) => char.toUpperCase());
                                          const formatList = (items) => {
                                            if (!items.length) return '';
                                            if (items.length === 1) return items[0];
                                            if (items.length === 2) return `${items[0]} or ${items[1]}`;
                                            return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
                                          };
                                          const plans = Array.isArray(codSettings?.allowedTiers)
                                            ? codSettings.allowedTiers.map(titleCase)
                                            : [];
                                          let hint = null;
                                          if (codReason === 'PLAN_REQUIRED' && plans.length) {
                                            hint = <>Available on <strong>{formatList(plans)}</strong>. <a href="/upgrade" className="st-cod-hint-link">View plans</a></>;
                                          } else if (codReason === 'SUBSCRIPTION_EXPIRED') {
                                            hint = <>Your plan has lapsed. <a href="/upgrade" className="st-cod-hint-link">Renew to re-enable</a></>;
                                          } else if (codReason === 'ADMIN_SUSPENDED') {
                                            hint = <>Suspended by Revstar. <a href="mailto:support@userevstar.com" className="st-cod-hint-link">Contact support</a></>;
                                          } else if (codReason === 'PLATFORM_DISABLED') {
                                            hint = <>Rolling out across Revstar — check back soon.</>;
                                          } else if (codReason === 'TERMS_REQUIRED') {
                                            hint = <>Toggle on to accept the current terms.</>;
                                          }
                                          if (!hint) return null;
                                          return <p className="st-cod-hint">{hint}</p>;
                                        })()}
                                        {codEnabled && codAnalytics && (
                                          <div className="st-cod-analytics" aria-label="Cash on delivery analytics">
                                            <span><strong>{codAnalytics.orders || 0}</strong> COD orders</span>
                                            <span><strong>{codAnalytics.awaitingDecision || 0}</strong> awaiting</span>
                                            <span><strong>{formatCurrency(codAnalytics.cashCollected || 0, 'NGN')}</strong> collected</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className={`st-btn ${codEnabled ? 'st-btn-secondary' : 'st-btn-primary'}`}
                                      disabled={loadingCodSettings || togglingCod || codLocked}
                                      onClick={toggleCod}
                                    >
                                      {codLocked && <Lock size={14} />}
                                      {togglingCod ? '…' : codAction}
                                    </button>
                                  </div>
                                );
                              })()}
                            </div>
                          </>
                        )}
                  </div>
                </section>

                {/* Processing fees (fee payer per channel) */}
                <section className="st-card st-payment-fees st-payment-panel">
                  <div className="st-card-body is-tight">
                    <div className="st-card-head">
                      <div>
                        <h2 className="st-card-title">Processing fees</h2>
                        <p className="st-card-sub">Choose who covers processing fees on each channel.</p>
                      </div>
                    </div>
                    {loadingServiceCharge ? (
                          <div className="st-loading">Loading…</div>
                        ) : (
                          <>
                            <div className="st-fee-channel-list">
                              {serviceChargeChannels.length > 0 ? (
                                serviceChargeChannels.map((channelConfig) => {
                                  const currentWhoPays = serviceChargeChannelPreferences[channelConfig.channel] || channelConfig.whoPays || 'customer';
                                  return (
                                    <div className="st-fee-channel-row" key={channelConfig.channel}>
                                      <div className="st-fee-channel-copy">
                                        <strong>{channelConfig.label || channelConfig.channel}</strong>
                                        <span>{channelConfig.description}</span>
                                      </div>
                                      <div className="st-fee-channel-actions" role="radiogroup" aria-label={`${channelConfig.label || channelConfig.channel} transaction fee payer`}>
                                        <button
                                          type="button"
                                          role="radio"
                                          aria-checked={currentWhoPays === 'customer'}
                                          className={`st-fee-choice${currentWhoPays === 'customer' ? ' is-active' : ''}`}
                                          onClick={() => handleServiceChargeChannelChange(channelConfig.channel, 'customer')}
                                          disabled={savingServiceCharge}
                                        >
                                          {currentWhoPays === 'customer' && <Check size={13} />}
                                          Customer pays
                                        </button>
                                        <button
                                          type="button"
                                          role="radio"
                                          aria-checked={currentWhoPays === 'merchant'}
                                          className={`st-fee-choice${currentWhoPays === 'merchant' ? ' is-active' : ''}`}
                                          onClick={() => handleServiceChargeChannelChange(channelConfig.channel, 'merchant')}
                                          disabled={savingServiceCharge}
                                        >
                                          {currentWhoPays === 'merchant' && <Check size={13} />}
                                          I pay
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="st-radio-list" role="radiogroup">
                                  <button
                                    type="button"
                                    role="radio"
                                    aria-checked={serviceChargePayer === 'customer'}
                                    className={`st-radio-row${serviceChargePayer === 'customer' ? ' is-active' : ''}`}
                                    onClick={() => handleServiceChargePayerChange('customer')}
                                    disabled={savingServiceCharge}
                                  >
                                    <span className="st-radio-dot" />
                                    <span className="st-radio-body">
                                      <span className="st-radio-label">Customer pays</span>
                                      <span className="st-radio-desc">Added to the customer's checkout total. Your payout is unaffected.</span>
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    role="radio"
                                    aria-checked={serviceChargePayer === 'merchant'}
                                    className={`st-radio-row${serviceChargePayer === 'merchant' ? ' is-active' : ''}`}
                                    onClick={() => handleServiceChargePayerChange('merchant')}
                                    disabled={savingServiceCharge}
                                  >
                                    <span className="st-radio-dot" />
                                    <span className="st-radio-body">
                                      <span className="st-radio-label">I pay</span>
                                      <span className="st-radio-desc">Deducted from your settlement payout. Customer pays the listed price.</span>
                                    </span>
                                  </button>
                                </div>
                              )}
                            </div>
                            {serviceChargeSource === 'tier_default' && (
                              <p className="st-list-hint">Currently using platform defaults. Change a channel above to override who pays for that channel.</p>
                            )}
                          </>
                        )}
                  </div>
                </section>

                {/* ── Group: Payouts ─────────────────────────────────── */}
                <div className="st-payments-group-label">Payouts</div>

                {/* Bank transfer (Paystack Terminal) */}
                <section className="st-card st-payment-terminal st-payment-panel">
                  <div className="st-card-body is-tight">
                    <div className="st-card-head">
                      <div>
                        <h2 className="st-card-title">Bank transfer</h2>
                        <p className="st-card-sub">Dedicated account customers can transfer to. Funds settle into your wallet.</p>
                      </div>
                      {store?.paystackVirtualTerminalCode ? (
                        <span className="st-card-status">Active</span>
                      ) : store?.applicationStatus === 'APPROVED' ? (
                        <span className="st-card-status st-card-status-info">Provisioning</span>
                      ) : (
                        <span className="st-card-status st-card-status-warn">Pending approval</span>
                      )}
                    </div>

                        {store?.paystackVirtualTerminalCode && store?.virtualTerminalAccountNumber ? (
                          <div
                            role="button"
                            tabIndex={0}
                            className="st-terminal-card"
                            onClick={() => setShowTerminalModal(true)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setShowTerminalModal(true);
                              }
                            }}
                          >
                            <div className="st-terminal-card-head">
                              <div className="st-terminal-title">
                                <CreditCard size={18} />
                                <span>Paystack Terminal</span>
                              </div>
                              <ArrowRight size={18} className="st-terminal-chevron" />
                            </div>

                            <div className="st-terminal-card-body">
                              <div className="st-terminal-detail">
                                <span>Bank Name</span>
                                <strong>{store.virtualTerminalBank || 'Paystack-Titan'}</strong>
                              </div>
                              <div className="st-terminal-detail">
                                <span>Account Number</span>
                                <strong className="st-terminal-account">
                                  {store.virtualTerminalAccountNumber}
                                  <button
                                    type="button"
                                    className="st-terminal-copy-inline"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      copyTerminalValue(store.virtualTerminalAccountNumber, 'Account number');
                                    }}
                                  >
                                    <Copy size={15} />
                                    Copy
                                  </button>
                                </strong>
                              </div>
                              <div className="st-terminal-detail">
                                <span>Account Name</span>
                                <strong>{store.virtualTerminalAccountName || store?.name}</strong>
                              </div>
                            </div>

                            <div className="st-terminal-card-foot">
                              <span>Copy full account details</span>
                              <button
                                type="button"
                                className="st-terminal-copy-icon"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  copyTerminalDetails();
                                }}
                                aria-label="Copy full account details"
                              >
                                <Copy size={18} />
                              </button>
                            </div>
                          </div>
                        ) : store?.paystackVirtualTerminalCode ? (
                          <div className="st-dva-inline">
                            <div className="st-dva-inline-cell">
                              <p className="st-dva-label">Terminal</p>
                              <p className="st-dva-value">{store.virtualTerminalName || 'Paystack Terminal'}</p>
                            </div>
                          </div>
                        ) : store?.applicationStatus === 'APPROVED' ? (
                          <p className="st-card-sub" style={{ margin: 0 }}>
                            Your Paystack Terminal is being provisioned. Check back shortly.
                          </p>
                        ) : (
                          <p className="st-card-sub" style={{ margin: 0 }}>
                            Provisioned automatically once your store is approved.
                          </p>
                        )}

                        {store?.virtualAccountNumber && (
                          <p className="st-card-sub" style={{ margin: '4px 0 0' }}>
                            Legacy DVA remains available for older bank-transfer payments and reconciliation.
                          </p>
                        )}
                  </div>
                </section>

                {/* Settlement bank (where payouts land) */}
                <section className="st-card st-payment-bank st-payment-panel">
                  <div className="st-card-body is-tight">
                    <div className="st-card-head">
                      <div>
                        <h2 className="st-card-title">Settlement bank</h2>
                        <p className="st-card-sub">Where your payouts arrive. This is the account we send withdrawals to.</p>
                      </div>
                    </div>

                        {bankAcct ? (
                          <div className="st-bank-card">
                            <div className="st-bank-card-head">
                              <div className="st-bank-icon"><Landmark size={18} /></div>
                              <h4 className="st-bank-card-name">{kycStatus.bankName || 'Bank'}</h4>
                              {kycStatus.accountVerified ? (
                                <span className="st-bank-card-pill"><Check size={11} /> Verified</span>
                              ) : (
                                <span className="st-bank-card-pill st-bank-card-pill-warn">Unverified</span>
                              )}
                            </div>
                            <div className="st-bank-card-body">
                              <div className="st-bank-kv">
                                <span className="st-bank-kv-label">Account number</span>
                                <span className="st-bank-kv-value">
                                  <span className="st-bank-kv-mono">
                                    {revealedBankNumber ? bankAcct : `•••• ${bankLast4}`}
                                  </span>
                                  <button
                                    type="button"
                                    className="st-bank-show"
                                    onClick={handleBankReveal}
                                  >
                                    {revealedBankNumber ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Show</>}
                                  </button>
                                </span>
                              </div>
                              {kycStatus.accountName && (
                                <div className="st-bank-kv">
                                  <span className="st-bank-kv-label">Name</span>
                                  <span className="st-bank-kv-value">{kycStatus.accountName}</span>
                                </div>
                              )}
                            </div>
                            {revealedBankNumber && (
                              <div className="st-bank-reveal-notice">
                                <Eye size={11} /> Hides automatically in 30 seconds.
                              </div>
                            )}
                            <div className="st-bank-card-foot">
                              <button
                                type="button"
                                className="st-bank-card-cta"
                                onClick={() => setShowChangeBankModal(true)}
                              >
                                Change bank <ArrowRight size={14} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="st-bank-empty">
                            <AlertCircle size={16} />
                            <span>No settlement bank linked yet — add one to receive payouts.</span>
                          </div>
                        )}
                  </div>
                </section>

                  {/* Paystack Terminal details modal */}
                  {store?.paystackVirtualTerminalCode && store?.virtualTerminalAccountNumber && (
                    <Modal
                      isOpen={showTerminalModal}
                      onClose={() => setShowTerminalModal(false)}
                      title="Paystack Terminal"
                      size="sm"
                    >
                      <div className="st-terminal-modal">
                        <div className="st-terminal-modal-card">
                          <div className="st-terminal-detail">
                            <span>Bank Name</span>
                            <strong>{store.virtualTerminalBank || 'Paystack-Titan'}</strong>
                          </div>
                          <div className="st-terminal-detail">
                            <span>Account Number</span>
                            <strong className="st-terminal-account">
                              {store.virtualTerminalAccountNumber}
                              <button
                                type="button"
                                className="st-terminal-copy-inline"
                                onClick={() => copyTerminalValue(store.virtualTerminalAccountNumber, 'Account number')}
                              >
                                <Copy size={15} />
                                Copy
                              </button>
                            </strong>
                          </div>
                          <div className="st-terminal-detail">
                            <span>Account Name</span>
                            <strong>{store.virtualTerminalAccountName || store?.name}</strong>
                          </div>
                          <div className="st-terminal-detail">
                            <span>Terminal Code</span>
                            <strong className="st-dva-value-mono">{store.paystackVirtualTerminalCode}</strong>
                          </div>
                          <button type="button" className="st-terminal-copy-full" onClick={copyTerminalDetails}>
                            <Copy size={16} />
                            Copy full account details
                          </button>
                        </div>

                        <div className="st-terminal-test-note">
                          <strong>Test with a small transfer first.</strong>
                          <p>Send a small amount to this terminal account, then confirm it appears in Wallet under Paystack Terminal transactions.</p>
                        </div>

                        <div className="st-terminal-modal-actions">
                          <button type="button" className="st-terminal-action" onClick={viewTerminalTransactions}>
                            <Wallet size={17} />
                            <span>
                              <strong>View transactions</strong>
                              <small>Open wallet filtered to Paystack Terminal payments.</small>
                            </span>
                          </button>
                          <button
                            type="button"
                            className="st-terminal-action"
                            onClick={handleDownloadTerminalPoster}
                            disabled={terminalPosterDownloading}
                          >
                            <Download size={17} />
                            <span>
                              <strong>{terminalPosterDownloading ? 'Preparing banner' : 'Download Terminal poster'}</strong>
                              <small>Get a high-resolution JPG banner for your store.</small>
                            </span>
                          </button>
                        </div>
                      </div>
                    </Modal>
                  )}

                {/* ── Tax ──────────────────────────────────────────────── */}
                <section className="st-card st-payment-tax st-payment-panel">
                  <div className="st-card-body is-tight">
                    <div className="st-card-head">
                      <div>
                        <h2 className="st-card-title">Merchant tax</h2>
                        <p className="st-card-sub">Configure tax collected on your own order sales.</p>
                      </div>
                    </div>
                    {loadingTaxSettings ? (
                      <div className="st-loading">Loading…</div>
                    ) : (
                      <>
                        <div className="st-toggle-row">
                          <div className="st-toggle-text">
                            <span className="st-toggle-label">Apply tax at checkout</span>
                            <span className="st-toggle-sub">
                              {taxSettingsStatusText}
                            </span>
                          </div>
                          <label className="st-toggle">
                            <input
                              type="checkbox"
                              checked={!!taxSettings.enabled}
                              onChange={(e) => setTaxSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
                              disabled={savingTaxSettings}
                            />
                            <span className="st-toggle-track" />
                          </label>
                        </div>

                        <div className="st-grid st-grid-2col" style={{ marginTop: 12 }}>
                          <label className="st-field">
                            <span className="st-field-label">Tax name</span>
                            <input
                              className="st-input"
                              value={taxSettings.name || ''}
                              placeholder="VAT"
                              onChange={(e) => setTaxSettings((prev) => ({ ...prev, name: e.target.value }))}
                              disabled={!taxSettings.enabled || savingTaxSettings}
                            />
                          </label>
                          <label className="st-field">
                            <span className="st-field-label">Rate %</span>
                            <input
                              className="st-input"
                              type="number"
                              min="0"
                              max="100"
                              step="0.001"
                              value={taxSettings.rate ?? 0}
                              onChange={(e) => setTaxSettings((prev) => ({ ...prev, rate: e.target.value }))}
                              disabled={!taxSettings.enabled || savingTaxSettings}
                            />
                          </label>
                        </div>

                        <label className="st-check-row" style={{ marginTop: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!taxSettings.inclusive}
                            onChange={(e) => setTaxSettings((prev) => ({ ...prev, inclusive: e.target.checked }))}
                            disabled={!taxSettings.enabled || savingTaxSettings}
                          />
                          <span className="st-check-row-text">
                            <span className="st-check-label">Prices already include this tax</span>
                            <span className="st-check-sub">When enabled, Revstar records the tax portion without increasing the customer total.</span>
                          </span>
                        </label>

                        {taxSettings.enabled && Number(taxSettings.rate) > 0 && (
                          <p className="st-list-hint">
                            <Percent size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            ₦10,000 order {taxSettings.inclusive ? 'includes' : 'adds'} ₦{(
                              taxSettings.inclusive
                                ? 10000 - (10000 / (1 + (Number(taxSettings.rate) / 100)))
                                : 10000 * (Number(taxSettings.rate) / 100)
                            ).toFixed(2)} {taxSettings.name || 'tax'}.
                          </p>
                        )}

                        <div className="st-card-foot">
                          <button
                            type="button"
                            className="st-btn st-btn-primary"
                            onClick={saveTaxSettings}
                            disabled={savingTaxSettings}
                          >
                            {savingTaxSettings ? 'Saving…' : taxSettingsDirty ? 'Save tax changes' : 'Save tax settings'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>

                {/* ── Tips (POS) ───────────────────────────────────────── */}
                <section className="st-card st-payment-tips st-payment-panel">
                  <div className="st-card-body is-tight">
                    <div className="st-card-head">
                      <div><h2 className="st-card-title">Tips (POS)</h2></div>
                    </div>
                    {loadingTipSettings ? (
                      <div className="st-loading">Loading…</div>
                    ) : (
                      <>
                        <div className="st-toggle-row">
                          <div className="st-toggle-text">
                            <span className="st-toggle-label">Enable tips</span>
                          </div>
                          <label className="st-toggle">
                            <input
                              type="checkbox"
                              checked={!!tipSettings.enabled}
                              onChange={() => setTipSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                            />
                            <span className="st-toggle-track" />
                          </label>
                        </div>

                        {tipSettings.enabled && (
                          <>
                            <div className="st-toggle-row">
                              <div className="st-toggle-text">
                                <span className="st-toggle-label">Track by employee</span>
                              </div>
                              <label className="st-toggle">
                                <input
                                  type="checkbox"
                                  checked={!!tipSettings.trackByEmployee}
                                  onChange={() => setTipSettings(prev => ({ ...prev, trackByEmployee: !prev.trackByEmployee }))}
                                />
                                <span className="st-toggle-track" />
                              </label>
                            </div>
                            <div className="st-toggle-row">
                              <div className="st-toggle-text">
                                <span className="st-toggle-label">Allow custom amounts</span>
                              </div>
                              <label className="st-toggle">
                                <input
                                  type="checkbox"
                                  checked={!!tipSettings.allowCustom}
                                  onChange={() => setTipSettings(prev => ({ ...prev, allowCustom: !prev.allowCustom }))}
                                />
                                <span className="st-toggle-track" />
                              </label>
                            </div>

                            <div className="st-field">
                              <label className="st-field-label">Suggested amounts (₦)</label>
                              <div className="st-tip-chips">
                                {(tipSettings.suggestedAmounts || []).map((amount, idx) => (
                                  <div key={idx} className="st-tip-chip">
                                    <input
                                      type="number"
                                      min="0"
                                      className="st-input"
                                      value={amount}
                                      onChange={(e) => {
                                        const newAmounts = [...tipSettings.suggestedAmounts];
                                        newAmounts[idx] = parseInt(e.target.value) || 0;
                                        setTipSettings(prev => ({ ...prev, suggestedAmounts: newAmounts }));
                                      }}
                                    />
                                    {tipSettings.suggestedAmounts.length > 1 && (
                                      <button
                                        type="button"
                                        className="st-tip-chip-remove"
                                        onClick={() => {
                                          const newAmounts = tipSettings.suggestedAmounts.filter((_, i) => i !== idx);
                                          setTipSettings(prev => ({ ...prev, suggestedAmounts: newAmounts }));
                                        }}
                                        aria-label="Remove"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {(tipSettings.suggestedAmounts || []).length < 5 && (
                                  <button
                                    type="button"
                                    className="st-tip-chip-add"
                                    onClick={() => {
                                      const newAmounts = [...(tipSettings.suggestedAmounts || []), 0];
                                      setTipSettings(prev => ({ ...prev, suggestedAmounts: newAmounts }));
                                    }}
                                  >
                                    <Plus size={14} /> Add
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        <div className="st-card-foot">
                          <button
                            type="button"
                            className="st-btn st-btn-primary"
                            disabled={savingTipSettings}
                            onClick={saveTipSettings}
                          >
                            {savingTipSettings ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>

              </div>
              );
            })()}

            {activeTab === 'billing' && (() => {
              const sub = billingData.subscription;
              const invoiceStatusPill = (status) => {
                if (status === 'PAID') return 'st-pill st-pill-success';
                if (status === 'OVERDUE') return 'st-pill st-pill-danger';
                return 'st-pill st-pill-warn';
              };
              return (
              <div className="st-stack">
                {loadingBilling ? (
                  <div className="st-loading">Loading billing…</div>
                ) : (
                  <>
                    {/* ── Subscription ──────────────────────────────────── */}
                    {sub && (
                      <section className="st-card">
                        <div className="st-card-body is-tight">
                          {sub.isOnTrial && (
                            <div className="st-banner st-banner-warn st-banner-block">
                              <span className="st-banner-icon"><Clock size={16} /></span>
                              <div className="st-banner-body">
                                <p className="st-banner-title">
                                  Free trial — {sub.trialDaysRemaining || 0} day{sub.trialDaysRemaining !== 1 ? 's' : ''} left
                                </p>
                                {sub.trialEndsAt && (
                                  <p className="st-banner-text">Ends on {formatDate(sub.trialEndsAt)}</p>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="st-sub-head">
                            <div style={{ minWidth: 0 }}>
                              <h2 className="st-card-title">
                                {sub.currentTier} plan
                                {sub.isOnTrial && <span className="st-tier-trial">Trial</span>}
                              </h2>
                              {sub.expiresAt && !sub.isOnTrial && (
                                <p className="st-card-sub">
                                  {new Date(sub.expiresAt) > new Date()
                                    ? `Renews ${formatDate(sub.expiresAt)}`
                                    : `Expired ${formatDate(sub.expiresAt)}`}
                                </p>
                              )}
                            </div>
                            {sub.tierDetails && sub.tierDetails.price !== undefined && sub.tierDetails.price !== null && (
                              <div className="st-price">
                                <span className="st-price-amount">{formatCurrency(sub.tierDetails.price, sub.tierDetails.currency)}</span>
                                <span className="st-price-period">/{sub.tierDetails.billingPeriod || 'month'}</span>
                              </div>
                            )}
                          </div>

                          <div className="st-sub-actions">
                            {sub.isOnTrial && (
                              <button
                                type="button"
                                className="st-btn st-btn-primary"
                                onClick={() => navigateToUpgrade(navigate)}
                              >
                                Convert to paid
                              </button>
                            )}
                            {!sub.isOnTrial && sub.canUpgrade && (
                              <button
                                type="button"
                                className="st-btn st-btn-primary"
                                onClick={() => navigateToUpgrade(navigate)}
                              >
                                Upgrade plan
                              </button>
                            )}
                            {!sub.isOnTrial && sub.canDowngrade && !sub.canUpgrade && (
                              <button
                                type="button"
                                className="st-btn st-btn-secondary"
                                onClick={() => navigate('/upgrade')}
                              >
                                Downgrade plan
                              </button>
                            )}
                            {!sub.isOnTrial && !sub.canUpgrade && !sub.canDowngrade && (
                              <span className="st-card-status">Top tier</span>
                            )}
                          </div>

                          {sub.activeSubscription && (
                            <div className="st-toggle-row">
                              <div className="st-toggle-text">
                                <span className="st-toggle-label">Auto-renew</span>
                                <span className="st-toggle-sub">Charge the default card when this period ends.</span>
                              </div>
                              <label className="st-toggle">
                                <input
                                  type="checkbox"
                                  checked={!!sub.activeSubscription?.autoRenew}
                                  onChange={handleToggleAutoRenew}
                                  disabled={togglingAutoRenew}
                                />
                                <span className="st-toggle-track" />
                              </label>
                            </div>
                          )}

                          {sub.tierDetails?.features && (
                            <p className="st-card-sub" style={{ margin: 0 }}>
                              <strong>Includes:</strong> {sub.tierDetails.features}
                            </p>
                          )}

                          {sub.billingCredit > 0 && (
                            <div className="st-credit-row">
                              <div className="st-credit-icon"><Coins size={18} /></div>
                              <div className="st-credit-body">
                                <p className="st-credit-label">Available credit</p>
                                <p className="st-credit-amount">{formatCurrency(sub.billingCredit, 'NGN')}</p>
                              </div>
                              <span className="st-pill st-pill-success">Applied on upgrade</span>
                            </div>
                          )}
                        </div>
                      </section>
                    )}

                    {/* ── Payment methods ──────────────────────────────── */}
                    <section className="st-card">
                      <div className="st-card-body is-tight">
                        <div className="st-card-head st-card-head-row">
                          <div><h2 className="st-card-title">Payment methods</h2></div>
                          <button
                            type="button"
                            className="st-btn st-btn-secondary"
                            onClick={handleAddCard}
                            disabled={addingCard}
                          >
                            {addingCard ? 'Adding…' : <><Plus size={14} /> Add card</>}
                          </button>
                        </div>
                        {billingData.paymentMethods.filter(m => !removedCardIds.includes(m.id)).length > 0 ? (
                          <div className="st-gateway-list">
                            {billingData.paymentMethods.filter(m => !removedCardIds.includes(m.id)).map((m) => (
                              <div key={m.id} className={`st-gateway-row${m.isDefault ? ' is-active' : ''}`}>
                                <div className="st-gateway-icon"><CreditCard size={18} /></div>
                                <div className="st-gateway-body">
                                  <h4 className="st-gateway-name">
                                    {m.brand || m.type} •••• {m.last4}
                                    {m.isDefault && (
                                      <span className="st-pill st-pill-success" style={{ marginLeft: 8 }}>Default</span>
                                    )}
                                  </h4>
                                  <p className="st-gateway-desc">
                                    {m.expiryMonth && m.expiryYear
                                      ? `Expires ${m.expiryMonth}/${m.expiryYear}`
                                      : `Added ${formatDate(m.createdAt)}`}
                                  </p>
                                </div>
                                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                  {!m.isDefault && (
                                    <button
                                      type="button"
                                      className="st-btn st-btn-ghost"
                                      onClick={() => handleSetDefaultCard(m.id)}
                                      disabled={settingDefaultId === m.id}
                                    >
                                      {settingDefaultId === m.id ? 'Setting…' : 'Set default'}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="st-btn st-btn-danger"
                                    onClick={() => handleRemoveCard(m.id)}
                                    disabled={removingCardId === m.id}
                                  >
                                    {removingCardId === m.id ? '…' : 'Remove'}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="st-empty">No cards saved. Add one to enable auto-renewal.</div>
                        )}
                      </div>
                    </section>

                    {/* ── Billing history ──────────────────────────────── */}
                    <section className="st-card">
                      <div className="st-card-body is-tight">
                        <div className="st-card-head st-card-head-row">
                          <div><h2 className="st-card-title">Billing history</h2></div>
                          <button
                            type="button"
                            className="st-btn st-btn-ghost"
                            onClick={() => setShowBillingHistory(!showBillingHistory)}
                          >
                            {showBillingHistory ? 'Hide' : 'View'}
                          </button>
                        </div>
                        {showBillingHistory && (
                          billingData.invoices.length > 0 ? (
                            <div className="st-invoice-list">
                              {billingData.invoices.map((inv) => (
                                <div key={inv.id} className="st-invoice-row">
                                  <div className="st-invoice-body">
                                    <p className="st-invoice-title">{inv.description}</p>
                                    <p className="st-invoice-meta">{formatDate(inv.date)}</p>
                                  </div>
                                  <div className="st-invoice-tags">
                                    <span className={invoiceStatusPill(inv.status)}>{inv.status}</span>
                                  </div>
                                  <p className="st-invoice-amount">{formatCurrency(inv.amount, inv.currency)}</p>
                                  <div className="st-invoice-cta">
                                    <button
                                      type="button"
                                      className="st-btn st-btn-ghost"
                                      onClick={() => handleDownloadInvoice(inv.id)}
                                    >
                                      <Download size={14} /> View
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="st-empty">No billing history yet.</div>
                          )
                        )}
                      </div>
                    </section>

                    {/* ── POS service fees ─────────────────────────────── */}
                    {posFeeData.balance && (
                      <section className="st-card">
                        <div className="st-card-body is-tight">
                          <div className="st-card-head st-card-head-row">
                            <div>
                              <h2 className="st-card-title">
                                POS service fees
                                {posFeeData.status?.isBlocked && (
                                  <span className="st-pill st-pill-danger" style={{ marginLeft: 8 }}>POS blocked</span>
                                )}
                              </h2>
                            </div>
                            <button
                              type="button"
                              className="st-btn st-btn-ghost"
                              onClick={fetchPosFeeData}
                              disabled={loadingPosFees}
                            >
                              <RefreshCw size={14} /> {loadingPosFees ? '…' : 'Refresh'}
                            </button>
                          </div>

                          {posFeeData.status?.isBlocked && (
                            <div className="st-banner st-banner-error st-banner-block">
                              <span className="st-banner-icon"><AlertTriangle size={16} /></span>
                              <div className="st-banner-body">
                                <p className="st-banner-title">POS access blocked</p>
                                <p className="st-banner-text">
                                  Your POS is blocked due to overdue service fees — pay the outstanding invoices below to restore access.
                                  {posFeeData.status?.blockReason && <> Reason: {posFeeData.status.blockReason}.</>}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="st-stat-grid">
                            <div className="st-stat-tile">
                              <p className="st-stat-label">Outstanding</p>
                              <p className={`st-stat-value${posFeeData.balance?.currentBalance > 0 ? ' st-stat-value-danger' : ' st-stat-value-success'}`}>
                                {formatCurrency(posFeeData.balance?.currentBalance || 0, 'NGN')}
                              </p>
                            </div>
                            <div className="st-stat-tile">
                              <p className="st-stat-label">Total paid</p>
                              <p className="st-stat-value st-stat-value-success">
                                {formatCurrency(posFeeData.balance?.totalPaid || 0, 'NGN')}
                              </p>
                            </div>
                            <div className="st-stat-tile">
                              <p className="st-stat-label">Overdue invoices</p>
                              <p className={`st-stat-value${posFeeData.status?.overdueInvoices > 0 ? ' st-stat-value-danger' : ' st-stat-value-success'}`}>
                                {posFeeData.status?.overdueInvoices || 0}
                              </p>
                            </div>
                          </div>

                          <div className="st-field">
                            <label className="st-field-label">Invoice billing cycle</label>
                            <select
                              className="st-select"
                              value={posFeeData.settings?.invoiceCycle || 'WEEKLY'}
                              onChange={(e) => handleUpdatePosSettings(e.target.value)}
                              disabled={savingPosSettings}
                            >
                              {posFeeData.availableCycles.map(cycle => (
                                <option key={cycle.value} value={cycle.value}>{cycle.label}</option>
                              ))}
                            </select>
                            {posFeeData.settings?.nextInvoiceAt && (
                              <span className="st-field-hint">
                                Next invoice: {new Date(posFeeData.settings.nextInvoiceAt).toLocaleDateString('en-NG', {
                                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                })}
                              </span>
                            )}
                          </div>

                          {posFeeData.invoices.length > 0 && (
                            <div>
                              <p className="st-section-label">Recent invoices</p>
                              <div className="st-invoice-list">
                                {posFeeData.invoices.map(inv => {
                                  const payable = inv.status === 'PENDING' || inv.status === 'OVERDUE';
                                  return (
                                    <div key={inv.id} className={`st-invoice-row${inv.status === 'OVERDUE' ? ' is-overdue' : ''}`}>
                                      <div className="st-invoice-body">
                                        <p className="st-invoice-title">{inv.invoiceNumber}</p>
                                        <p className="st-invoice-meta">
                                          {new Date(inv.periodStart).toLocaleDateString()} – {new Date(inv.periodEnd).toLocaleDateString()}
                                        </p>
                                      </div>
                                      <div className="st-invoice-tags">
                                        <span className={invoiceStatusPill(inv.status)}>{inv.status}</span>
                                      </div>
                                      <p className="st-invoice-amount">{formatCurrency(inv.amount, inv.currency)}</p>
                                      <div className="st-invoice-cta">
                                        {payable && (
                                          <button
                                            type="button"
                                            className="st-btn st-btn-primary"
                                            onClick={() => handlePayPosInvoice(inv.id)}
                                            disabled={payingInvoice === inv.id}
                                          >
                                            {payingInvoice === inv.id ? '…' : 'Pay now'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {posFeeData.invoices.length === 0 && !loadingPosFees && (
                            <div className="st-empty">No POS fee invoices yet.</div>
                          )}
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
              );
            })()}

          </div>
        </div>
      </div>

      {/* Phase 4.A.6 follow-on — bulk markup recalculator */}
      <RecalculatePricesModal
        isOpen={showRecalculateModal}
        onClose={() => setShowRecalculateModal(false)}
      />

      {/* Password Change Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        }}
        title="Change Password"
      >
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Current Password
              </label>
              <div style={{ position: 'relative' }}>
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280',
                    fontSize: '18px',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showCurrentPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                </button>
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280',
                    fontSize: '18px',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showNewPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                </button>
              </div>
              <p style={{
                fontSize: '12px',
                color: '#6B7280',
                margin: '4px 0 0 0'
              }}>
                Must contain: 8+ characters, uppercase, lowercase, number, and special character
              </p>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Confirm New Password
              </label>
              <div style={{ position: 'relative' }}>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280',
                    fontSize: '18px',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showConfirmPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                </button>
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '16px',
              borderTop: '1px solid #E5E7EB'
            }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                disabled={changingPassword}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordChange}
                disabled={changingPassword}
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Invoice Modal */}
      <Modal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
        }}
        title="Invoice Details"
      >
        {selectedInvoice && (
          <div style={{ padding: '20px' }}>
            <div style={{
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '16px',
                paddingBottom: '16px',
                borderBottom: '2px solid #E5E7EB'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#111827',
                    margin: '0 0 4px 0'
                  }}>
                    {selectedInvoice.invoiceNumber}
                  </h3>
                  <p style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    margin: 0
                  }}>
                    {formatDate(selectedInvoice.date)}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: selectedInvoice.paymentStatus === 'PAID' ? '#F0FDF4' : selectedInvoice.paymentStatus === 'PENDING' ? '#FEF3C7' : '#FEE2E2',
                    color: selectedInvoice.paymentStatus === 'PAID' ? '#166534' : selectedInvoice.paymentStatus === 'PENDING' ? '#92400E' : '#991B1B',
                    display: 'inline-block'
                  }}>
                    {selectedInvoice.paymentStatus}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6B7280',
                  margin: '0 0 8px 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Customer Details
                </h4>
                <div style={{ fontSize: '14px', color: '#111827' }}>
                  <div>{selectedInvoice.customer.name}</div>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>{selectedInvoice.customer.email}</div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6B7280',
                  margin: '0 0 12px 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Items
                </h4>
                {selectedInvoice.items.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      marginBottom: '8px'
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {item.description}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        Qty: {item.quantity}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                      {formatCurrency(item.total, selectedInvoice.currency)}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                borderTop: '2px solid #E5E7EB',
                paddingTop: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  <span>Tax</span>
                  <span>{formatCurrency(selectedInvoice.tax, selectedInvoice.currency)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '12px',
                  borderTop: '1px solid #E5E7EB',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827'
                }}>
                  <span>Total</span>
                  <span>{formatCurrency(selectedInvoice.total, selectedInvoice.currency)}</span>
                </div>
              </div>

              {selectedInvoice.paymentReference && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#F0FDF4',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#166534'
                }}>
                  <strong>Payment Reference:</strong> {selectedInvoice.paymentReference}
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowInvoiceModal(false);
                  setSelectedInvoice(null);
                }}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  window.print();
                }}
              >
                Print Invoice
              </Button>
            </div>
          </div>
        )}
      </Modal>


      {/* Password Confirmation Modal for Bank Account Viewing */}
      <PasswordConfirmModal
        isOpen={showPasswordConfirmModal}
        onClose={handlePasswordModalClose}
        onConfirm={handlePasswordConfirm}
        title="Confirm Your Password"
        message="Please enter your password to view your full bank account number"
      />

      {/* Change Bank Account Modal */}
      <ChangeBankAccountModal
        isOpen={showChangeBankModal}
        onClose={() => setShowChangeBankModal(false)}
        onSuccess={handleChangeBankAccountSuccess}
        currentAccount={kycStatus ? {
          accountNumber: kycStatus.accountNumber,
          bankName: kycStatus.bankName
        } : null}
      />

      {/* COD enable modal — a product moment, not a generic confirm */}
      <Modal
        isOpen={showCodEnableModal}
        onClose={() => { if (!togglingCod) { setShowCodEnableModal(false); setCodTermsAccepted(false); } }}
        title="Turn on cash on delivery"
        size="sm"
        padded={false}
      >
        <div className="st-cod-modal">
          <div className="st-cod-modal-hero">
            <div className="st-cod-modal-icon" aria-hidden="true">
              <Coins size={20} />
            </div>
            <div className="st-cod-modal-badge">No service charge</div>
          </div>
          <p className="st-cod-modal-lede">
            Let customers check out without paying online. You confirm the order, arrange
            delivery, and collect cash — Revstar doesn’t take a cut.
          </p>
          <ul className="st-cod-modal-list">
            <li>
              <Check size={14} />
              <span>You accept or decline each new order before preparing it.</span>
            </li>
            <li>
              <Check size={14} />
              <span>You arrange delivery (merchant rider or customer pickup) and collect cash.</span>
            </li>
            <li>
              <AlertCircle size={14} />
              <span>Revstar can’t guarantee payment on COD orders — you own the risk.</span>
            </li>
          </ul>
          <label className="st-cod-modal-terms">
            <input
              type="checkbox"
              checked={codTermsAccepted}
              onChange={(event) => setCodTermsAccepted(event.target.checked)}
            />
            <span>I accept the <strong>Cash on Delivery terms</strong> for my store.</span>
          </label>
          <div className="st-cod-modal-actions">
            <button
              type="button"
              className="st-btn st-btn-ghost"
              onClick={() => { setShowCodEnableModal(false); setCodTermsAccepted(false); }}
              disabled={togglingCod}
            >
              Not now
            </button>
            <button
              type="button"
              className="st-btn st-btn-primary"
              onClick={() => commitCodSetting(true)}
              disabled={!codTermsAccepted || togglingCod}
            >
              {togglingCod ? 'Enabling…' : 'Enable COD'}
            </button>
          </div>
        </div>
      </Modal>

    </Shell>
  );
}
